import React, { useState, useEffect, useRef } from 'react';
import { CourtPOI, SportType, PickupGame, AthleteProfile } from '../types';
import { triggerHaptic } from '../utils/haptics';
import { CourtSpecificLeaderboard } from './CourtSpecificLeaderboard';
import { CourtStatusBadgeTooltip } from './common/CourtStatusBadgeTooltip';
import L from 'leaflet';
import { 
  MapPin, 
  Users, 
  Zap, 
  Sun, 
  Flame, 
  Search, 
  CheckCircle2, 
  AlertTriangle, 
  Maximize2,
  Compass,
  Plus,
  Trophy,
  Filter,
  Layers,
  Activity,
  Sparkles,
  TrendingUp,
  RefreshCw,
  Check,
  Radio,
  Clock,
  QrCode,
  Camera,
  Scan,
  Bell,
  Send,
  X,
  ShieldCheck,
  Play,
  Pause,
  BarChart2,
  ChevronRight,
  Building2,
  TreePine,
  Droplet,
  Car,
  SlidersHorizontal,
  LogOut,
  Star,
  MessageSquare,
  ThumbsUp,
  Navigation,
  Heart
} from 'lucide-react';

interface CourtMapViewProps {
  courts: CourtPOI[];
  pickupGames: PickupGame[];
  onHostAtCourt: (court: CourtPOI) => void;
  onTriggerGeofenceAlert: (court: CourtPOI) => void;
  onOpenCreatePark: () => void;
  favoriteCourtIds?: string[];
  onToggleFavoriteCourt?: (courtId: string) => void;
  user?: AthleteProfile;
  athletes?: AthleteProfile[];
  onSelectAthlete?: (athlete: AthleteProfile) => void;
}

export interface CourtHeatInfo {
  totalPlayers: number;
  maxCapacity: number;
  occupancyRatio: number;
  occupancyPct: number;
  heatLevel: 'empty' | 'moderate' | 'busy' | 'packed';
  colorHex: string;
  bgTailwind: string;
  borderTailwind: string;
  textTailwind: string;
  badgeBg: string;
  badgeText: string;
  glowClass: string;
  badgeLabel: string;
  badgeIcon: string;
  activeGamesCount: number;
}

export interface PopularityBadgeInfo {
  label: 'Hopping' | 'Active' | 'Quiet';
  labelWithEmoji: string;
  badgeStyle: string;
  badgeBgSolid: string;
  badgeTextColor: string;
  dotColor: string;
}

export const getPopularityBadge = (activePlayersNowCount: number): PopularityBadgeInfo => {
  if (activePlayersNowCount >= 8) {
    return {
      label: 'Hopping',
      labelWithEmoji: 'Hopping 🔥',
      badgeStyle: 'bg-rose-500/25 text-rose-300 border-rose-500/50 shadow-rose-500/20',
      badgeBgSolid: 'bg-rose-500 text-white',
      badgeTextColor: 'text-rose-400',
      dotColor: 'bg-rose-500',
    };
  }
  if (activePlayersNowCount >= 3) {
    return {
      label: 'Active',
      labelWithEmoji: 'Active ⚡',
      badgeStyle: 'bg-amber-400/25 text-amber-300 border-amber-400/50 shadow-amber-400/20',
      badgeBgSolid: 'bg-amber-400 text-black',
      badgeTextColor: 'text-amber-400',
      dotColor: 'bg-amber-400',
    };
  }
  return {
    label: 'Quiet',
    labelWithEmoji: 'Quiet 🌙',
    badgeStyle: 'bg-emerald-500/25 text-emerald-300 border-emerald-500/50 shadow-emerald-500/20',
    badgeBgSolid: 'bg-emerald-500 text-white',
    badgeTextColor: 'text-emerald-400',
    dotColor: 'bg-emerald-500',
  };
};

export const getCourtHeatInfo = (
  court: CourtPOI,
  pickupGames: PickupGame[],
  extraPlayers: number = 0,
  isCheckedIn: boolean = false
): CourtHeatInfo => {
  const courtGames = pickupGames.filter((g) => g.courtId === court.id);
  const gamePlayers = courtGames.reduce((acc, g) => acc + g.currentPlayers, 0);
  const totalPlayers = (court.activePlayersNow || 0) + gamePlayers + extraPlayers + (isCheckedIn ? 1 : 0);

  // Default max capacities by sport
  let maxCapacity = 16;
  if (court.sport === 'basketball') maxCapacity = 15;
  else if (court.sport === 'baseball' || court.sport === 'softball') maxCapacity = 24;
  else if (court.sport === 'soccer') maxCapacity = 22;
  else if (court.sport === 'pickleball') maxCapacity = 12;
  else if (court.sport === 'volleyball') maxCapacity = 12;

  const occupancyRatio = Math.min(1.2, totalPlayers / maxCapacity);
  const occupancyPct = Math.min(100, Math.round((totalPlayers / maxCapacity) * 100));

  if (occupancyPct >= 80 || totalPlayers >= 14) {
    return {
      totalPlayers,
      maxCapacity,
      occupancyRatio,
      occupancyPct,
      heatLevel: 'packed',
      colorHex: '#ef4444', // Red
      bgTailwind: 'bg-rose-500',
      borderTailwind: 'border-rose-400',
      textTailwind: 'text-rose-400',
      badgeBg: 'bg-rose-500 text-white',
      badgeText: 'text-rose-300',
      glowClass: 'shadow-rose-500/50 ring-2 ring-rose-500 animate-pulse',
      badgeLabel: 'Packed / High Heat',
      badgeIcon: '🔴',
      activeGamesCount: courtGames.length,
    };
  }

  if (occupancyPct >= 50 || totalPlayers >= 8) {
    return {
      totalPlayers,
      maxCapacity,
      occupancyRatio,
      occupancyPct,
      heatLevel: 'busy',
      colorHex: '#f97316', // Orange
      bgTailwind: 'bg-orange-500',
      borderTailwind: 'border-orange-400',
      textTailwind: 'text-orange-400',
      badgeBg: 'bg-orange-500 text-white',
      badgeText: 'text-orange-300',
      glowClass: 'shadow-orange-500/40 ring-1 ring-orange-400',
      badgeLabel: 'Busy / Active',
      badgeIcon: '🟠',
      activeGamesCount: courtGames.length,
    };
  }

  if (occupancyPct >= 20 || totalPlayers >= 3) {
    return {
      totalPlayers,
      maxCapacity,
      occupancyRatio,
      occupancyPct,
      heatLevel: 'moderate',
      colorHex: '#facc15', // Yellow/Amber
      bgTailwind: 'bg-amber-400',
      borderTailwind: 'border-amber-300',
      textTailwind: 'text-amber-400',
      badgeBg: 'bg-amber-400 text-black',
      badgeText: 'text-amber-300',
      glowClass: 'shadow-amber-400/30 ring-1 ring-amber-300',
      badgeLabel: 'Moderate Activity',
      badgeIcon: '🟡',
      activeGamesCount: courtGames.length,
    };
  }

  return {
    totalPlayers,
    maxCapacity,
    occupancyRatio,
    occupancyPct,
    heatLevel: 'empty',
    colorHex: '#22c55e', // Green
    bgTailwind: 'bg-emerald-500',
    borderTailwind: 'border-emerald-400',
    textTailwind: 'text-emerald-400',
    badgeBg: 'bg-emerald-500 text-white',
    badgeText: 'text-emerald-300',
    glowClass: 'shadow-emerald-500/20 ring-1 ring-emerald-400/50',
    badgeLabel: 'Empty / Quiet',
    badgeIcon: '🟢',
    activeGamesCount: courtGames.length,
  };
};

export const CourtMapView: React.FC<CourtMapViewProps> = ({
  courts,
  pickupGames,
  onHostAtCourt,
  onTriggerGeofenceAlert,
  onOpenCreatePark,
  favoriteCourtIds = [],
  onToggleFavoriteCourt,
  user,
  athletes = [],
  onSelectAthlete,
}) => {
  const [selectedCourt, setSelectedCourt] = useState<CourtPOI>(courts[0]);
  const [activePopupCourtId, setActivePopupCourtId] = useState<string | null>(courts[0]?.id || null);
  const [sportFilter, setSportFilter] = useState<SportType | 'all'>('all');
  const [surfaceFilter, setSurfaceFilter] = useState<string>('all');
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);
  const [heatFilter, setHeatFilter] = useState<'all' | 'empty' | 'moderate' | 'busy' | 'packed'>('all');
  const [popularityFilter, setPopularityFilter] = useState<'all' | 'Hopping' | 'Active' | 'Quiet'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [checkedInCourts, setCheckedInCourts] = useState<string[]>([]);
  const [maxDistanceKm, setMaxDistanceKm] = useState<number>(25);
  const [isLeaderboardModalOpen, setIsLeaderboardModalOpen] = useState<boolean>(false);

  // Regional Location Filter States (City / State auto-center & POI filter)
  const [regionalFilter, setRegionalFilter] = useState<'my_city' | 'my_state' | 'all'>('my_city');
  const [customCityFilter, setCustomCityFilter] = useState<string>(user?.registeredCity || 'New York');
  const [customStateFilter, setCustomStateFilter] = useState<string>(user?.registeredState || 'NY');

  // 'Near Me' Browser Geolocation Filter State (Within 5 miles)
  const [isNearMeActive, setIsNearMeActive] = useState<boolean>(false);
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [isLocating, setIsLocating] = useState<boolean>(false);

  // OpenStreetMap Leaflet state & refs
  const [mapMode, setMapMode] = useState<'openstreetmap' | 'grid'>('openstreetmap');
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const leafletMapRef = useRef<L.Map | null>(null);
  const markersGroupRef = useRef<L.LayerGroup | null>(null);

  // Haversine distance in Miles
  const calculateDistanceMiles = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 3958.8; // Radius of Earth in miles
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(R * c * 10) / 10;
  };

  const handleToggleNearMe = () => {
    triggerHaptic('medium');
    if (!isNearMeActive) {
      setIsLocating(true);
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserCoords({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
            setIsLocating(false);
            setIsNearMeActive(true);
            triggerHaptic('success');
          },
          (error) => {
            console.warn('Geolocation positioning error or permission denied:', error);
            // Fallback to center or first court coords
            setUserCoords({
              lat: courts[0]?.lat || 40.7128,
              lng: courts[0]?.lng || -74.0060,
            });
            setIsLocating(false);
            setIsNearMeActive(true);
          },
          { timeout: 5000, enableHighAccuracy: true }
        );
      } else {
        setUserCoords({
          lat: courts[0]?.lat || 40.7128,
          lng: courts[0]?.lng || -74.0060,
        });
        setIsNearMeActive(true);
      }
    } else {
      setIsNearMeActive(false);
    }
  };

  // Auto-update regional location filters when user prop updates
  useEffect(() => {
    if (user?.registeredCity) {
      setCustomCityFilter(user.registeredCity);
    }
    if (user?.registeredState) {
      setCustomStateFilter(user.registeredState);
    }
  }, [user?.registeredCity, user?.registeredState]);

  // Auto-center selected court on first matching regional court when filter updates
  useEffect(() => {
    if (filteredCourts.length > 0) {
      const match = filteredCourts.find((c) => c.id === selectedCourt?.id) || filteredCourts[0];
      if (match && match.id !== selectedCourt?.id) {
        setSelectedCourt(match);
        setActivePopupCourtId(match.id);
      }
    }
  }, [regionalFilter, customCityFilter, customStateFilter]);
  const [locationTypeFilter, setLocationTypeFilter] = useState<'all' | 'indoor' | 'outdoor'>('all');
  const [lightingFilter, setLightingFilter] = useState<'all' | 'has_lights' | 'no_lights'>('all');
  const [rimFilter, setRimFilter] = useState<string>('all');
  const [waterFountainOnly, setWaterFountainOnly] = useState<boolean>(false);
  const [parkingOnly, setParkingOnly] = useState<boolean>(false);
  const [showAttributeFilterDrawer, setShowAttributeFilterDrawer] = useState<boolean>(false);
  const [showAdvancedSimulator, setShowAdvancedSimulator] = useState<boolean>(false);

  // Active attribute filter count
  const activeAttributeFiltersCount =
    (locationTypeFilter !== 'all' ? 1 : 0) +
    (lightingFilter !== 'all' ? 1 : 0) +
    (rimFilter !== 'all' ? 1 : 0) +
    (waterFountainOnly ? 1 : 0) +
    (parkingOnly ? 1 : 0);

  const handleResetAttributeFilters = () => {
    setLocationTypeFilter('all');
    setLightingFilter('all');
    setRimFilter('all');
    setWaterFountainOnly(false);
    setParkingOnly(false);
  };

  const handleGetDirections = (court: CourtPOI, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const destination = encodeURIComponent(`${court.name}, ${court.address}`);
    const isAppleDevice = typeof navigator !== 'undefined' && /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent);
    const directionsUrl = isAppleDevice
      ? `https://maps.apple.com/?daddr=${court.lat},${court.lng}&q=${destination}`
      : `https://www.google.com/maps/dir/?api=1&destination=${court.lat},${court.lng}&destination_place_id=${destination}`;

    window.open(directionsUrl, '_blank', 'noopener,noreferrer');
  };

  // Real-time simulated player additions & Live Engine
  const [extraPlayersMap, setExtraPlayersMap] = useState<Record<string, number>>({});
  const [showHeatGlowOverlay, setShowHeatGlowOverlay] = useState(true);
  const [isLiveSimulationActive, setIsLiveSimulationActive] = useState<boolean>(true);
  const [timePreset, setTimePreset] = useState<'afternoon' | 'peak_rush' | 'morning_quiet'>('peak_rush');
  const [lastUpdatedTime, setLastUpdatedTime] = useState<Date>(new Date());

  // Live simulation ticker event log
  const [liveHeatLogs, setLiveHeatLogs] = useState<Array<{ id: string; time: string; courtName: string; text: string; level: 'surge' | 'drop' | 'checkin' }>>([
    { id: '1', time: '13:04', courtName: 'Rucker Park', text: '🔥 Heat Surge: +4 athletes checked in for 5v5 pickup!', level: 'surge' },
    { id: '2', time: '13:02', courtName: 'Lincoln Park Courts', text: '⚡ 2 new players arrived at Pickleball Court 1', level: 'checkin' },
    { id: '3', time: '12:58', courtName: 'West 4th Courts', text: '👥 Game ended: 3 players left court', level: 'drop' },
  ]);

  // Live Simulation Engine Effect: Periodic dynamic heat fluctuation
  useEffect(() => {
    if (!isLiveSimulationActive || courts.length === 0) return;

    const interval = setInterval(() => {
      // Pick a random court
      const randomCourt = courts[Math.floor(Math.random() * courts.length)];
      const deltas = timePreset === 'peak_rush' ? [2, 3, 4, -1, 3] : [1, 2, -2, 1, -1];
      const delta = deltas[Math.floor(Math.random() * deltas.length)];

      setExtraPlayersMap((prev) => {
        const current = prev[randomCourt.id] || 0;
        const nextValue = Math.max(0, current + delta);
        return { ...prev, [randomCourt.id]: nextValue };
      });

      const nowStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setLastUpdatedTime(new Date());

      // Create log text
      let logText = '';
      let logType: 'surge' | 'drop' | 'checkin' = 'checkin';
      if (delta > 0) {
        logText = `🔥 Live Heat Change: +${delta} athlete${delta > 1 ? 's' : ''} arrived at ${randomCourt.name}!`;
        logType = delta >= 3 ? 'surge' : 'checkin';
      } else {
        logText = `📉 Heat Cool: ${Math.abs(delta)} player${Math.abs(delta) > 1 ? 's' : ''} left ${randomCourt.name}.`;
        logType = 'drop';
      }

      setLiveHeatLogs((prevLogs) => [
        {
          id: `log_${Date.now()}`,
          time: nowStr,
          courtName: randomCourt.name,
          text: logText,
          level: logType,
        },
        ...prevLogs.slice(0, 5),
      ]);
    }, 5000);

    return () => clearInterval(interval);
  }, [isLiveSimulationActive, courts, timePreset]);

  // Preset Trigger: Peak Rush Surge
  const handleTriggerPeakSurge = () => {
    const updatedMap: Record<string, number> = { ...extraPlayersMap };
    courts.forEach((c) => {
      const surgeCount = Math.floor(Math.random() * 5) + 3;
      updatedMap[c.id] = (updatedMap[c.id] || 0) + surgeCount;
    });
    setExtraPlayersMap(updatedMap);
    setLastUpdatedTime(new Date());

    const nowStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    setLiveHeatLogs((prev) => [
      {
        id: `surge_${Date.now()}`,
        time: nowStr,
        courtName: 'All Venue Courts',
        text: '🚀 PEAK HOUR RUSH DETECTED: +25 Athletes checked in across city parks!',
        level: 'surge',
      },
      ...prev.slice(0, 5),
    ]);
  };

  // Reset Fluctuation
  const handleResetHeatSimulation = () => {
    setExtraPlayersMap({});
    setLastUpdatedTime(new Date());
  };

  // Calculate heat for selected court
  const selectedCourtHeat = getCourtHeatInfo(
    selectedCourt,
    pickupGames,
    extraPlayersMap[selectedCourt.id] || 0,
    checkedInCourts.includes(selectedCourt.id)
  );

  const selectedActiveCount = selectedCourt.activePlayersNow !== undefined
    ? selectedCourt.activePlayersNow + (extraPlayersMap[selectedCourt.id] || 0)
    : selectedCourtHeat.totalPlayers;

  const selectedPopularity = getPopularityBadge(selectedActiveCount);

  const filteredCourts = courts.filter((court) => {
    const matchesSport = sportFilter === 'all' || court.sport === sportFilter;
    const matchesSurface =
      surfaceFilter === 'all' ||
      (court.surfaceType && court.surfaceType.toLowerCase().includes(surfaceFilter.toLowerCase()));
    const matchesSearch =
      court.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      court.address.toLowerCase().includes(searchQuery.toLowerCase());

    const heat = getCourtHeatInfo(
      court,
      pickupGames,
      extraPlayersMap[court.id] || 0,
      checkedInCourts.includes(court.id)
    );

    const matchesHeat = heatFilter === 'all' || heat.heatLevel === heatFilter;

    const activeCount = court.activePlayersNow !== undefined ? court.activePlayersNow + (extraPlayersMap[court.id] || 0) : heat.totalPlayers;
    const popularity = getPopularityBadge(activeCount);
    const matchesPopularity = popularityFilter === 'all' || popularity.label === popularityFilter;

    // Attribute Filters
    const matchesLocation =
      locationTypeFilter === 'all' ||
      (locationTypeFilter === 'indoor' && court.isIndoor) ||
      (locationTypeFilter === 'outdoor' && !court.isIndoor);

    const matchesLighting =
      lightingFilter === 'all' ||
      (lightingFilter === 'has_lights' && court.lighting) ||
      (lightingFilter === 'no_lights' && !court.lighting);

    const matchesRim =
      rimFilter === 'all' ||
      (court.rimCondition && court.rimCondition.toLowerCase().includes(rimFilter.toLowerCase()));

    const matchesWater = !waterFountainOnly || court.waterFountain === true;
    const matchesParking = !parkingOnly || court.parkingAvailable === true;

    const matchesDistance = maxDistanceKm >= 50 || court.distanceKm <= maxDistanceKm;

    // Regional City/State Location Filter
    let matchesRegion = true;
    if (regionalFilter === 'my_city') {
      const cityQuery = (customCityFilter || user?.registeredCity || 'New York').toLowerCase().trim();
      const addr = (court.address || '').toLowerCase();
      const name = (court.name || '').toLowerCase();
      matchesRegion = addr.includes(cityQuery) || name.includes(cityQuery);
    } else if (regionalFilter === 'my_state') {
      const stateQuery = (customStateFilter || user?.registeredState || 'NY').toLowerCase().trim();
      const addr = (court.address || '').toLowerCase();
      matchesRegion = addr.includes(stateQuery) || addr.includes(` ${stateQuery}`) || addr.includes(`, ${stateQuery}`);
    }

    // 'Near Me' 5-mile radius filter
    if (isNearMeActive) {
      const uLat = userCoords?.lat || courts[0]?.lat || 40.7128;
      const uLng = userCoords?.lng || courts[0]?.lng || -74.0060;
      const distMiles = calculateDistanceMiles(uLat, uLng, court.lat, court.lng);
      if (distMiles > 5.0) return false;
    }

    return (
      matchesRegion &&
      matchesSport &&
      matchesSurface &&
      matchesSearch &&
      matchesHeat &&
      matchesPopularity &&
      matchesLocation &&
      matchesLighting &&
      matchesRim &&
      matchesWater &&
      matchesParking &&
      matchesDistance
    );
  }).sort((a, b) => {
    if (isNearMeActive) {
      const uLat = userCoords?.lat || courts[0]?.lat || 40.7128;
      const uLng = userCoords?.lng || courts[0]?.lng || -74.0060;
      const distA = calculateDistanceMiles(uLat, uLng, a.lat, a.lng);
      const distB = calculateDistanceMiles(uLat, uLng, b.lat, b.lng);
      return distA - distB;
    }
    return 0;
  });

  useEffect(() => {
    if (mapMode !== 'openstreetmap' || !mapContainerRef.current) return;

    const centerLat = selectedCourt?.lat || (courts[0]?.lat ?? 40.73061);
    const centerLng = selectedCourt?.lng || (courts[0]?.lng ?? -73.935242);

    if (!leafletMapRef.current) {
      if ((mapContainerRef.current as any)._leaflet_id) {
        (mapContainerRef.current as any)._leaflet_id = null;
      }
      try {
        const map = L.map(mapContainerRef.current, {
          center: [centerLat, centerLng],
          zoom: 13,
          zoomControl: true,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(map);

        leafletMapRef.current = map;
        markersGroupRef.current = L.layerGroup().addTo(map);
      } catch (err) {
        console.warn('CourtMapView Leaflet map init warning:', err);
      }
    } else {
      try {
        leafletMapRef.current.setView([centerLat, centerLng]);
      } catch (_) {}
    }

    if (markersGroupRef.current) {
      try {
        markersGroupRef.current.clearLayers();

        filteredCourts.forEach((court) => {
          const isSel = selectedCourt?.id === court.id;
          const isCheckedIn = checkedInCourts.includes(court.id);
          const extraPlayers = extraPlayersMap[court.id] || 0;
          const heat = getCourtHeatInfo(court, pickupGames, extraPlayers, isCheckedIn);

          let pinColor = '#22c55e';
          if (heat.occupancyPct >= 80 || heat.totalPlayers >= 14) pinColor = '#ef4444';
          else if (heat.occupancyPct >= 35 || heat.totalPlayers >= 5) pinColor = '#f59e0b';

          const isVerified = court.status === 'verified';
          
          // Distinct marker styling & badges based on Verified vs Pending status
          const statusBadgeHtml = isVerified
            ? `<span style="background-color: rgba(16, 185, 129, 0.25); color: #6ee7b7; border: 1px solid rgba(16, 185, 129, 0.5); border-radius: 9999px; padding: 2px 7px; font-size: 9px; font-weight: 900; display: inline-flex; align-items: center; gap: 3px;">🛡️ Verified</span>`
            : `<span style="background-color: rgba(245, 158, 11, 0.25); color: #fcd34d; border: 1px dashed rgba(245, 158, 11, 0.6); border-radius: 9999px; padding: 2px 7px; font-size: 9px; font-weight: 900; display: inline-flex; align-items: center; gap: 3px;">⏳ Pending</span>`;

          const borderStyle = isSel
            ? '2px solid #a3e635'
            : isVerified
            ? '2px solid #10b981'
            : '2px dashed #f59e0b';

          const customHtml = `
            <div style="background-color: #030712; border: ${borderStyle}; border-radius: 14px; padding: 5px 9px; color: #ffffff; font-family: sans-serif; font-size: 11px; font-weight: 800; display: flex; align-items: center; gap: 6px; box-shadow: 0 4px 16px rgba(0,0,0,0.7); cursor: pointer; white-space: nowrap; transform: ${isSel ? 'scale(1.08)' : 'scale(1.0)'}; transition: transform 0.2s;">
              <span style="font-size: 14px;">${
                court.sport === 'basketball' ? '🏀' :
                court.sport === 'soccer' ? '⚽' :
                court.sport === 'pickleball' ? '🏓' : '🏐'
              }</span>
              <span style="color: #f8fafc;">${court.name.length > 12 ? court.name.slice(0, 12) + '...' : court.name}</span>
              ${statusBadgeHtml}
              <span style="background-color: ${pinColor}; color: #000000; padding: 1px 6px; border-radius: 10px; font-size: 10px; font-weight: 900;">${heat.totalPlayers}</span>
            </div>
          `;

          const icon = L.divIcon({
            html: customHtml,
            className: 'court-leaflet-marker',
            iconSize: [215, 34],
            iconAnchor: [107, 17],
          });

          const marker = L.marker([court.lat, court.lng], { icon });

          const popupContent = `
            <div style="font-family: sans-serif; color: #0f172a; padding: 6px; min-width: 210px; text-align: left;">
              <div style="display: flex; align-items: center; justify-content: space-between; gap: 6px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 6px;">
                <span style="background-color: ${isVerified ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)'}; color: ${isVerified ? '#047857' : '#b45309'}; border: 1px solid ${isVerified ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}; border-radius: 9999px; padding: 2px 8px; font-size: 10px; font-weight: 900; text-transform: uppercase;">
                  ${isVerified ? '🛡️ Official Verified Court' : '⏳ Pending Scout Review'}
                </span>
              </div>

              <h4 style="font-size: 14px; font-weight: 900; margin: 0 0 2px 0; color: #0f172a;">${court.name}</h4>
              <p style="font-size: 11px; color: #475569; margin: 0 0 8px 0;">${court.address}</p>

              <div style="font-size: 10px; color: #334155; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 6px; margin-bottom: 8px; line-height: 1.4;">
                <strong style="color: ${isVerified ? '#047857' : '#b45309'};">${isVerified ? 'Validation Criteria Met:' : 'Requirements for Verified Status:'}</strong><br/>
                ${
                  isVerified
                    ? 'Inspected for official rim height, durable surface, safety netting, geofenced GPS bounds & evening lighting.'
                    : 'Submitted by players. Requires scout inspection, 3 court photos & 3 verified game check-ins.'
                }
              </div>

              <div style="font-size: 11px; font-weight: 800; color: ${pinColor}; background-color: #f1f5f9; padding: 5px 8px; border-radius: 8px; display: flex; align-items: center; justify-content: space-between; gap: 4px;">
                <span>${heat.badgeLabel}</span>
                <span style="background-color: #0f172a; color: #ffffff; padding: 1px 6px; border-radius: 6px; font-size: 10px; font-weight: 900;">${heat.totalPlayers} Active</span>
              </div>
            </div>
          `;

          marker.bindPopup(popupContent);

          marker.on('click', () => {
            setSelectedCourt(court);
            setActivePopupCourtId(court.id);
          });

          markersGroupRef.current?.addLayer(marker);
        });
      } catch (err) {
        console.warn('CourtMapView marker rendering warning:', err);
      }
    }

    return () => {
      if (leafletMapRef.current) {
        try {
          leafletMapRef.current.remove();
        } catch (_) {}
        leafletMapRef.current = null;
      }
    };
  }, [mapMode, filteredCourts, selectedCourt, pickupGames, checkedInCourts, extraPlayersMap]);

  // QR Code Scanner state
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [targetQrCourt, setTargetQrCourt] = useState<CourtPOI>(courts[0]);
  const [isSimulatingScan, setIsSimulatingScan] = useState(false);
  const [broadcastNotification, setBroadcastNotification] = useState<string | null>(null);

  // Leave Court & Session Review Flow State
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [leaveTargetCourt, setLeaveTargetCourt] = useState<CourtPOI | null>(null);
  const [sessionIntensity, setSessionIntensity] = useState<'Casual' | 'Moderate' | 'High Intensity' | 'Pro Competitive'>('High Intensity');
  const [sessionRating, setSessionRating] = useState<number>(5);
  const [sessionReviewNote, setSessionReviewNote] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<string[]>(['Rims Clean', 'Great Competition']);
  const [checkoutToast, setCheckoutToast] = useState<string | null>(null);

  const handleCheckIn = (courtId: string) => {
    triggerHaptic('success');
    if (!checkedInCourts.includes(courtId)) {
      setCheckedInCourts([...checkedInCourts, courtId]);
    }
  };

  const handleOpenLeaveModal = (court: CourtPOI) => {
    triggerHaptic('medium');
    setLeaveTargetCourt(court);
    setIsLeaveModalOpen(true);
  };

  const toggleConditionTag = (tag: string) => {
    triggerHaptic('light');
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleExecuteCheckout = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leaveTargetCourt) return;
    triggerHaptic('warning');

    const court = leaveTargetCourt;

    // 1. Remove from checked-in list if checked in
    setCheckedInCourts((prev) => prev.filter((id) => id !== court.id));

    // 2. Update court's real-time occupancy data (decrement extra players)
    setExtraPlayersMap((prev) => {
      const current = prev[court.id] || 0;
      return { ...prev, [court.id]: Math.max(0, current - 1) };
    });

    // 3. Log event into live heat ticker stream
    const nowStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const tagSummary = selectedTags.length > 0 ? ` • ${selectedTags.join(', ')}` : '';
    setLiveHeatLogs((prev) => [
      {
        id: Date.now().toString(),
        time: nowStr,
        courtName: court.name,
        text: `🏁 Player checked out • Session: ${sessionIntensity} (${sessionRating}⭐)${tagSummary}`,
        level: 'drop',
      },
      ...prev.slice(0, 8),
    ]);

    // 4. Show success toast notification
    setCheckoutToast(
      `🏁 Successfully checked out of ${court.name}! Real-time occupancy updated (-1 player). Session logged: ${sessionIntensity} (${sessionRating}⭐). +25 XP Earned!`
    );

    // Reset and close
    setIsLeaveModalOpen(false);
    setLeaveTargetCourt(null);
    setSessionReviewNote('');

    setTimeout(() => {
      setCheckoutToast(null);
    }, 6000);
  };

  const handleOpenQrScanner = (court?: CourtPOI) => {
    triggerHaptic('light');
    if (court) {
      setTargetQrCourt(court);
    }
    setIsQrModalOpen(true);
  };

  const handleExecuteQrCheckIn = (courtToScan: CourtPOI) => {
    triggerHaptic('medium');
    setIsSimulatingScan(true);
    setTimeout(() => {
      triggerHaptic('success');
      setIsSimulatingScan(false);
      if (!checkedInCourts.includes(courtToScan.id)) {
        setCheckedInCourts((prev) => [...prev, courtToScan.id]);
      }
      const courtGames = pickupGames.filter((g) => g.courtId === courtToScan.id);
      const notifiedCount = (courtToScan.activePlayersNow || 0) + courtGames.reduce((a, g) => a + g.currentPlayers, 0) + 2;
      
      const message = `📡 BROADCAST SENT: Notified ${notifiedCount} players at ${courtToScan.name} of your arrival! +50 XP Earned`;
      setBroadcastNotification(message);
      setIsQrModalOpen(false);

      setTimeout(() => {
        setBroadcastNotification(null);
      }, 7000);
    }, 1200);
  };

  const handleSimulateArrival = (courtId: string, delta: number) => {
    setExtraPlayersMap((prev) => {
      const current = prev[courtId] || 0;
      const updated = Math.max(0, current + delta);
      return { ...prev, [courtId]: updated };
    });
  };

  return (
    <div className="space-y-5 pb-20 lg:pb-8 animate-fadeIn text-white">
      {/* Top Streamlined Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-indigo-900/60 p-5 sm:p-6 rounded-[2.5rem] border border-white/10 shadow-xl">
        <div>
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-lime-400/20 text-lime-400 rounded-xl border border-lime-400/30">
              <Compass className="w-5 h-5 stroke-[2.5]" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-black italic uppercase tracking-tight text-white flex items-center gap-2">
                <span>Court Map & Live Occupancy</span>
                <span className="px-2.5 py-0.5 bg-lime-400 text-black text-[10px] font-mono font-black rounded-full uppercase">
                  {filteredCourts.length} Courts
                </span>
              </h1>
              <p className="text-xs text-indigo-200/80 mt-0.5 font-medium">
                Tap pins to see live player counts, check in, or host games near you.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {/* Sidebar Filter Toggle Button */}
          <button
            type="button"
            onClick={() => {
              triggerHaptic('light');
              setIsSidebarOpen(!isSidebarOpen);
            }}
            className={`px-3.5 py-2 rounded-xl font-black italic uppercase flex items-center space-x-1.5 text-xs transition tracking-wider border shadow-lg ${
              isSidebarOpen
                ? 'bg-lime-400 text-black border-lime-300 shadow-lime-400/20'
                : 'bg-indigo-950 hover:bg-indigo-900 text-lime-400 border-lime-400/40'
            }`}
            title="Toggle Sport & Surface Filter Sidebar"
          >
            <SlidersHorizontal className="w-4 h-4 stroke-[2.5]" />
            <span>{isSidebarOpen ? 'Filter Sidebar: ON' : 'Filter Sidebar'}</span>
          </button>

          {/* Near Me Geolocation Filter Toggle */}
          <button
            type="button"
            onClick={handleToggleNearMe}
            className={`px-3.5 py-2 rounded-xl font-black italic uppercase flex items-center space-x-1.5 text-xs transition tracking-wider shadow-lg ${
              isNearMeActive
                ? 'bg-sky-400 text-black ring-2 ring-sky-300 shadow-sky-400/30'
                : 'bg-indigo-950 hover:bg-indigo-900 text-sky-300 border border-sky-400/40'
            }`}
            title="Use browser geolocation to filter courts within 5 miles & sort by distance"
          >
            <Navigation className={`w-4 h-4 ${isLocating ? 'animate-spin text-black' : isNearMeActive ? 'text-black fill-black' : 'text-sky-400'}`} />
            <span>{isLocating ? 'Locating...' : isNearMeActive ? 'Courts Within 5 Mi ✓' : 'Find Courts Near Me (5 Mi)'}</span>
          </button>

          {/* Quick QR Scanner Button */}
          <button
            onClick={() => handleOpenQrScanner(selectedCourt)}
            className="px-3.5 py-2 bg-lime-400 hover:bg-lime-300 text-black font-black italic rounded-xl flex items-center space-x-1.5 text-xs transition uppercase tracking-wider shadow-lg shadow-lime-400/20"
          >
            <QrCode className="w-4 h-4 stroke-[2.5]" />
            <span>Scan QR</span>
          </button>

          <button
            onClick={onOpenCreatePark}
            className="px-3.5 py-2 bg-indigo-950 hover:bg-indigo-900 text-indigo-200 border border-white/15 font-bold italic rounded-xl flex items-center space-x-1.5 text-xs transition uppercase"
          >
            <Plus className="w-4 h-4 stroke-[2.5]" />
            <span>+ Add Park</span>
          </button>

          <button
            onClick={() => onTriggerGeofenceAlert(selectedCourt)}
            className="px-3.5 py-2 bg-indigo-950 hover:bg-indigo-900 text-rose-300 border border-rose-500/40 font-bold italic rounded-xl flex items-center space-x-1.5 text-xs transition uppercase"
          >
            <Zap className="w-4 h-4 text-rose-400 animate-pulse" />
            <span>Radar</span>
          </button>

          {/* Advanced Simulator Toggle */}
          <button
            onClick={() => setShowAdvancedSimulator(!showAdvancedSimulator)}
            className={`px-3 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-1.5 border ${
              showAdvancedSimulator
                ? 'bg-amber-400 text-black border-amber-300 shadow-md'
                : 'bg-indigo-950/90 text-indigo-300 border-white/10 hover:text-white'
            }`}
          >
            <Activity className="w-3.5 h-3.5" />
            <span>{showAdvancedSimulator ? 'Sim Controls: ON' : 'Live Heat Sim ⚙️'}</span>
          </button>
        </div>
      </div>

      {/* Broadcast Toast Notification Banner */}
      {broadcastNotification && (
        <div className="bg-gradient-to-r from-lime-400 via-emerald-400 to-lime-500 text-black p-3.5 rounded-2xl font-extrabold shadow-2xl flex items-center justify-between border-2 border-white/40">
          <div className="flex items-center space-x-3">
            <div className="p-1.5 bg-black/10 rounded-xl">
              <Bell className="w-4 h-4 text-black animate-spin" />
            </div>
            <div>
              <span className="text-[10px] uppercase font-black tracking-wider text-black/70 block">Court Check-in Broadcast</span>
              <p className="text-xs font-black italic">{broadcastNotification}</p>
            </div>
          </div>
          <button
            onClick={() => setBroadcastNotification(null)}
            className="p-1 rounded-xl hover:bg-black/10 text-black transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Check-Out Toast Banner */}
      {checkoutToast && (
        <div className="bg-gradient-to-r from-rose-500 via-pink-500 to-rose-600 text-white p-3.5 rounded-2xl font-extrabold shadow-2xl flex items-center justify-between border-2 border-white/40">
          <div className="flex items-center space-x-3">
            <div className="p-1.5 bg-white/20 rounded-xl">
              <LogOut className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="text-[10px] uppercase font-black tracking-wider text-rose-100 block">Court Session Review Recorded</span>
              <p className="text-xs font-black italic">{checkoutToast}</p>
            </div>
          </div>
          <button
            onClick={() => setCheckoutToast(null)}
            className="p-1 rounded-xl hover:bg-white/10 text-white transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Collapsible Advanced Heat Simulator Controls */}
      {showAdvancedSimulator && (
        <div className="bg-gradient-to-r from-indigo-950 via-indigo-900 to-indigo-950 p-4 rounded-2xl border border-lime-400/30 shadow-2xl space-y-3 animate-fadeIn">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <span className={`w-3 h-3 rounded-full inline-block ${isLiveSimulationActive ? 'bg-lime-400 animate-ping' : 'bg-rose-500'}`} />
                <span className={`w-3 h-3 rounded-full inline-block absolute inset-0 ${isLiveSimulationActive ? 'bg-lime-400' : 'bg-rose-500'}`} />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-black italic uppercase text-white tracking-wider">
                    Live Heat Simulation Engine
                  </span>
                  <span className="px-2 py-0.5 bg-lime-400/20 text-lime-400 border border-lime-400/40 text-[9px] font-mono font-extrabold rounded-full uppercase">
                    {isLiveSimulationActive ? 'Auto-Sync 5s' : 'Paused'}
                  </span>
                </div>
                <p className="text-[10px] text-indigo-300/80 font-medium">
                  Last updated: <span className="font-mono text-white font-bold">{lastUpdatedTime.toLocaleTimeString()}</span> • Testing player arrival flows
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setIsLiveSimulationActive(!isLiveSimulationActive)}
                className={`px-3 py-1 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-1 border ${
                  isLiveSimulationActive
                    ? 'bg-amber-400 text-black border-amber-300 shadow-md'
                    : 'bg-lime-400 text-black border-lime-300 shadow-md'
                }`}
              >
                {isLiveSimulationActive ? <Pause className="w-3.5 h-3.5 fill-black" /> : <Play className="w-3.5 h-3.5 fill-black" />}
                <span>{isLiveSimulationActive ? 'Pause' : 'Resume'}</span>
              </button>

              <button
                onClick={handleTriggerPeakSurge}
                className="px-3 py-1 bg-rose-500 hover:bg-rose-400 text-white font-black italic text-xs uppercase rounded-xl shadow-md flex items-center space-x-1 transition border border-rose-400"
              >
                <Flame className="w-3.5 h-3.5" />
                <span>Peak Surge (+25)</span>
              </button>

              <button
                onClick={handleResetHeatSimulation}
                className="px-2.5 py-1 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 border border-white/10 font-bold text-xs rounded-xl transition"
                title="Reset Heat"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>

              <button
                onClick={() => setShowHeatGlowOverlay(!showHeatGlowOverlay)}
                className={`px-2.5 py-1 rounded-xl text-xs font-bold uppercase transition border ${
                  showHeatGlowOverlay
                    ? 'bg-rose-500/20 text-rose-300 border-rose-400/50'
                    : 'bg-indigo-950 text-indigo-300 border-white/10'
                }`}
              >
                {showHeatGlowOverlay ? 'Glow ON' : 'Glow OFF'}
              </button>
            </div>
          </div>

          {/* Heat Index Legend Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs font-bold pt-1 border-t border-white/10">
            <div className="p-1.5 bg-emerald-500/15 border border-emerald-400/30 rounded-xl flex items-center justify-center space-x-1">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              <span className="text-emerald-300 font-black text-[10px] uppercase">🟢 Low (&lt;25%)</span>
            </div>
            <div className="p-1.5 bg-amber-400/15 border border-amber-300/30 rounded-xl flex items-center justify-center space-x-1">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
              <span className="text-amber-300 font-black text-[10px] uppercase">🟡 Mod (25-50%)</span>
            </div>
            <div className="p-1.5 bg-orange-500/15 border border-orange-400/30 rounded-xl flex items-center justify-center space-x-1">
              <span className="w-2.5 h-2.5 rounded-full bg-orange-500" />
              <span className="text-orange-300 font-black text-[10px] uppercase">🟠 Busy (50-80%)</span>
            </div>
            <div className="p-1.5 bg-rose-500/15 border border-rose-400/30 rounded-xl flex items-center justify-center space-x-1">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" />
              <span className="text-rose-300 font-black text-[10px] uppercase">🔴 High (&gt;80%)</span>
            </div>
          </div>
        </div>
      )}

      {/* Filtering Sidebar + Main Split View Flex Container */}
      <div className="flex flex-col lg:flex-row gap-6 items-start">
        {/* Dedicated Court Filtering Sidebar */}
        {isSidebarOpen && (
          <aside className="w-full lg:w-80 shrink-0 bg-indigo-950/90 backdrop-blur-xl p-5 rounded-[2.5rem] border border-white/10 shadow-2xl space-y-5 animate-fadeIn">
            {/* Sidebar Header */}
            <div className="flex items-center justify-between pb-3 border-b border-white/10">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-lime-400/20 text-lime-400 rounded-xl border border-lime-400/30">
                  <SlidersHorizontal className="w-4 h-4 stroke-[2.5]" />
                </div>
                <div>
                  <h3 className="text-xs font-black italic uppercase tracking-wider text-white">Court Filters</h3>
                  <p className="text-[10px] text-indigo-300 font-medium">
                    Showing {filteredCourts.length} of {courts.length} venues
                  </p>
                </div>
              </div>

              {(sportFilter !== 'all' || surfaceFilter !== 'all' || heatFilter !== 'all' || searchQuery !== '') && (
                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('medium');
                    setSportFilter('all');
                    setSurfaceFilter('all');
                    setHeatFilter('all');
                    setPopularityFilter('all');
                    setSearchQuery('');
                  }}
                  className="text-[10px] font-bold uppercase text-rose-400 hover:text-rose-300 bg-rose-500/10 hover:bg-rose-500/20 px-2 py-1 rounded-lg border border-rose-500/20 transition"
                >
                  Reset
                </button>
              )}
            </div>

            {/* 1. Primary Sport Selection Sidebar Panel */}
            <div className="space-y-2">
              <label className="text-[11px] font-black uppercase tracking-wider text-lime-400 font-mono flex items-center justify-between">
                <span>Primary Sport</span>
                <span className="text-[10px] text-indigo-300/80 font-normal">Filter by game</span>
              </label>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { id: 'all', label: 'All Sports', icon: '🌐' },
                  { id: 'basketball', label: 'Basketball', icon: '🏀' },
                  { id: 'soccer', label: 'Soccer', icon: '⚽' },
                  { id: 'baseball', label: 'Baseball', icon: '⚾' },
                  { id: 'softball', label: 'Softball', icon: '🥎' },
                  { id: 'pickleball', label: 'Pickleball', icon: '🏓' },
                  { id: 'volleyball', label: 'Volleyball', icon: '🏐' },
                ].map((sp) => {
                  const isActive = sportFilter === sp.id;
                  return (
                    <button
                      key={sp.id}
                      type="button"
                      onClick={() => {
                        triggerHaptic('light');
                        setSportFilter(sp.id as any);
                      }}
                      className={`p-2 rounded-xl text-xs font-black italic uppercase transition-all duration-150 flex items-center space-x-1.5 border text-left ${
                        isActive
                          ? 'bg-lime-400 text-black border-lime-300 shadow-md shadow-lime-400/20 scale-[1.02]'
                          : 'bg-indigo-900/60 text-indigo-200 border-white/10 hover:bg-indigo-800 hover:text-white'
                      }`}
                    >
                      <span>{sp.icon}</span>
                      <span className="truncate">{sp.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 2. Court Surface Type Selector */}
            <div className="space-y-2">
              <label className="text-[11px] font-black uppercase tracking-wider text-lime-400 font-mono flex items-center justify-between">
                <span>Court Surface Type</span>
                <span className="text-[10px] text-indigo-300/80 font-normal">Material</span>
              </label>
              <div className="space-y-1.5">
                {[
                  { id: 'all', label: 'All Surface Types', icon: '🏢', desc: 'Any court flooring' },
                  { id: 'hardwood', label: 'Maple Hardwood / Gym', icon: '🪵', desc: 'Indoor polished hardwood' },
                  { id: 'asphalt', label: 'Asphalt / Street Court', icon: '🛣️', desc: 'Classic outdoor blacktop' },
                  { id: 'acrylic', label: 'Pro Acrylic Hard Court', icon: '🎾', desc: 'Smooth painted hard court' },
                  { id: 'turf', label: 'FieldTurf / Synthetic', icon: '🌿', desc: 'All-weather artificial turf' },
                  { id: 'clay', label: 'Clay / Dirt Infield', icon: '🧱', desc: 'Regulation clay Diamond/Court' },
                  { id: 'sand', label: 'Sand Court', icon: '🏖️', desc: 'Beach & outdoor sand court' },
                  { id: 'concrete', label: 'Concrete / Outdoor', icon: '🏛️', desc: 'Durable concrete courts' },
                ].map((surf) => {
                  const isActive = surfaceFilter === surf.id;
                  return (
                    <button
                      key={surf.id}
                      type="button"
                      onClick={() => {
                        triggerHaptic('light');
                        setSurfaceFilter(surf.id);
                      }}
                      className={`w-full p-2.5 rounded-2xl text-left border transition-all duration-150 flex items-center justify-between ${
                        isActive
                          ? 'bg-gradient-to-r from-lime-400 to-emerald-400 text-black border-lime-300 shadow-lg shadow-lime-400/20 font-black'
                          : 'bg-indigo-900/50 hover:bg-indigo-900 text-indigo-200 border-white/10'
                      }`}
                    >
                      <div className="flex items-center space-x-2 min-w-0">
                        <span className="text-base shrink-0">{surf.icon}</span>
                        <div className="min-w-0">
                          <span className={`text-xs block truncate ${isActive ? 'font-black uppercase italic' : 'font-bold text-white'}`}>
                            {surf.label}
                          </span>
                          <span className={`text-[10px] block truncate ${isActive ? 'text-black/80 font-medium' : 'text-indigo-300/70'}`}>
                            {surf.desc}
                          </span>
                        </div>
                      </div>
                      {isActive && <CheckCircle2 className="w-4 h-4 text-black shrink-0 ml-1 stroke-[3]" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Quick Summary Badge in Sidebar */}
            <div className="p-3 bg-indigo-900/40 rounded-2xl border border-white/10 text-[11px] space-y-1 text-indigo-200">
              <div className="flex items-center justify-between font-bold text-white">
                <span className="flex items-center gap-1">
                  <Compass className="w-3.5 h-3.5 text-lime-400" />
                  <span>Filtered Venues</span>
                </span>
                <span className="px-2 py-0.5 bg-lime-400 text-black font-mono font-black rounded-md text-[10px]">
                  {filteredCourts.length} Active
                </span>
              </div>
              <p className="text-[10px] text-indigo-300/70">
                Updating filters auto-refreshes map pins, distance metrics, and live occupancy heat maps.
              </p>
            </div>
          </aside>
        )}

        {/* Main Map + Court Detail Area */}
        <div className="flex-1 w-full min-w-0 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Interactive Map Canvas (2 cols) */}
        <div className="lg:col-span-2 bg-indigo-950 rounded-[2.5rem] overflow-hidden border border-white/10 shadow-xl relative min-h-[500px] flex flex-col justify-between p-4">
          
          {/* Map Top Filters & Search Overlay */}
          <div className="z-10 flex flex-col space-y-2 bg-indigo-900/95 backdrop-blur-md p-3.5 rounded-2xl border border-white/10 shadow-xl">
            {/* Regional Location Sync Bar */}
            <div className="p-2.5 bg-indigo-950/90 rounded-xl border border-lime-400/30 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center space-x-2 text-xs">
                <MapPin className="w-4 h-4 text-lime-400 shrink-0" />
                <span className="font-black italic uppercase text-white tracking-wider text-[11px]">
                  Region POI Filter:
                </span>
                <span className="text-[10px] text-indigo-200/80 font-semibold hidden md:inline">
                  Auto-centers map on local courts & venues
                </span>
              </div>

              <div className="flex items-center space-x-1.5 flex-wrap">
                <button
                  type="button"
                  onClick={() => {
                    setRegionalFilter('my_city');
                    setCustomCityFilter(user?.registeredCity || 'New York');
                  }}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase transition flex items-center space-x-1 border ${
                    regionalFilter === 'my_city'
                      ? 'bg-lime-400 text-black border-lime-300 shadow'
                      : 'bg-indigo-900 text-indigo-300 border-white/10 hover:text-white'
                  }`}
                >
                  <Building2 className="w-3 h-3" />
                  <span>City ({customCityFilter || user?.registeredCity || 'New York'})</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setRegionalFilter('my_state');
                    setCustomStateFilter(user?.registeredState || 'NY');
                  }}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase transition flex items-center space-x-1 border ${
                    regionalFilter === 'my_state'
                      ? 'bg-lime-400 text-black border-lime-300 shadow'
                      : 'bg-indigo-900 text-indigo-300 border-white/10 hover:text-white'
                  }`}
                >
                  <Compass className="w-3 h-3" />
                  <span>State ({customStateFilter || user?.registeredState || 'NY'})</span>
                </button>

                <button
                  type="button"
                  onClick={() => setRegionalFilter('all')}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase transition border ${
                    regionalFilter === 'all'
                      ? 'bg-lime-400 text-black border-lime-300 shadow'
                      : 'bg-indigo-900 text-indigo-300 border-white/10 hover:text-white'
                  }`}
                >
                  <span>All Regions 🌍</span>
                </button>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-2">
              <div className="relative flex-1 w-full flex items-center space-x-2">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-2.5 text-indigo-300/60" />
                  <input
                    type="text"
                    placeholder="Search court name, street address, or park..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 bg-indigo-950 text-white text-xs rounded-xl border border-white/10 outline-none focus:ring-1 focus:ring-lime-400"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleToggleNearMe}
                  className={`px-3 py-1.5 rounded-xl text-[10px] font-black italic uppercase transition flex items-center space-x-1 shrink-0 ${
                    isNearMeActive
                      ? 'bg-sky-400 text-black shadow-md shadow-sky-400/20'
                      : 'bg-indigo-950 text-sky-300 border border-sky-400/30 hover:border-sky-400'
                  }`}
                  title="Filter courts within 5 miles using browser location"
                >
                  <Navigation className={`w-3 h-3 ${isNearMeActive ? 'fill-black' : ''}`} />
                  <span>{isNearMeActive ? 'Near Me (<5 Mi)' : 'Near Me'}</span>
                </button>
              </div>

              {/* Heat Filter Buttons */}
              <div className="flex items-center bg-indigo-950 p-1 rounded-xl border border-white/10 text-[10px] font-black uppercase overflow-x-auto">
                <button
                  onClick={() => setHeatFilter('all')}
                  className={`px-2 py-1 rounded-lg transition shrink-0 ${
                    heatFilter === 'all' ? 'bg-indigo-800 text-white' : 'text-indigo-300/60 hover:text-white'
                  }`}
                >
                  All Heat
                </button>
                <button
                  onClick={() => setHeatFilter('empty')}
                  className={`px-2 py-1 rounded-lg transition shrink-0 ${
                    heatFilter === 'empty' ? 'bg-emerald-500 text-white' : 'text-indigo-300/60 hover:text-white'
                  }`}
                >
                  🟢 Empty
                </button>
                <button
                  onClick={() => setHeatFilter('moderate')}
                  className={`px-2 py-1 rounded-lg transition shrink-0 ${
                    heatFilter === 'moderate' ? 'bg-amber-400 text-black' : 'text-indigo-300/60 hover:text-white'
                  }`}
                >
                  🟡 Mod
                </button>
                <button
                  onClick={() => setHeatFilter('busy')}
                  className={`px-2 py-1 rounded-lg transition shrink-0 ${
                    heatFilter === 'busy' ? 'bg-orange-500 text-white' : 'text-indigo-300/60 hover:text-white'
                  }`}
                >
                  🟠 Busy
                </button>
                <button
                  onClick={() => setHeatFilter('packed')}
                  className={`px-2 py-1 rounded-lg transition shrink-0 ${
                    heatFilter === 'packed' ? 'bg-rose-500 text-white' : 'text-indigo-300/60 hover:text-white'
                  }`}
                >
                  🔴 Packed
                </button>
              </div>

              {/* Popularity Badge Filter Buttons */}
              <div className="flex items-center bg-indigo-950 p-1 rounded-xl border border-white/10 text-[10px] font-black uppercase overflow-x-auto">
                <span className="text-indigo-300/60 px-1 text-[9px] font-extrabold hidden md:inline">Popularity:</span>
                <button
                  onClick={() => setPopularityFilter('all')}
                  className={`px-2 py-1 rounded-lg transition shrink-0 ${
                    popularityFilter === 'all' ? 'bg-indigo-800 text-white' : 'text-indigo-300/60 hover:text-white'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setPopularityFilter('Hopping')}
                  className={`px-2 py-1 rounded-lg transition shrink-0 ${
                    popularityFilter === 'Hopping' ? 'bg-rose-500 text-white font-black shadow-sm' : 'text-indigo-300/60 hover:text-white'
                  }`}
                >
                  🔥 Hopping
                </button>
                <button
                  onClick={() => setPopularityFilter('Active')}
                  className={`px-2 py-1 rounded-lg transition shrink-0 ${
                    popularityFilter === 'Active' ? 'bg-amber-400 text-black font-black shadow-sm' : 'text-indigo-300/60 hover:text-white'
                  }`}
                >
                  ⚡ Active
                </button>
                <button
                  onClick={() => setPopularityFilter('Quiet')}
                  className={`px-2 py-1 rounded-lg transition shrink-0 ${
                    popularityFilter === 'Quiet' ? 'bg-emerald-500 text-white font-black shadow-sm' : 'text-indigo-300/60 hover:text-white'
                  }`}
                >
                  🌙 Quiet
                </button>
              </div>
            </div>

            {/* GPS Proximity Distance Filter Slider */}
            <div className="bg-indigo-950/80 p-2.5 rounded-xl border border-white/10 space-y-1.5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 text-xs">
                <div className="flex items-center space-x-2 text-indigo-100 font-extrabold">
                  <Compass className="w-3.5 h-3.5 text-lime-400 shrink-0" />
                  <span className="uppercase italic font-black text-white text-[11px]">Proximity Distance:</span>
                  <span className="px-2 py-0.5 bg-lime-400 text-black font-black font-mono rounded-md text-[10px] shadow">
                    {maxDistanceKm >= 50 ? 'All Courts (Any Distance)' : `Within ${maxDistanceKm} km`}
                  </span>
                </div>

                {/* Quick Distance Presets */}
                <div className="flex items-center space-x-1 text-[10px] font-black uppercase">
                  <span className="text-indigo-300/60 hidden sm:inline mr-1">Presets:</span>
                  <button
                    type="button"
                    onClick={() => setMaxDistanceKm(5)}
                    className={`px-2 py-0.5 rounded-md transition ${
                      maxDistanceKm === 5 ? 'bg-lime-400 text-black shadow' : 'bg-indigo-900 text-indigo-300 hover:text-white'
                    }`}
                  >
                    5 km
                  </button>
                  <button
                    type="button"
                    onClick={() => setMaxDistanceKm(10)}
                    className={`px-2 py-0.5 rounded-md transition ${
                      maxDistanceKm === 10 ? 'bg-lime-400 text-black shadow' : 'bg-indigo-900 text-indigo-300 hover:text-white'
                    }`}
                  >
                    10 km
                  </button>
                  <button
                    type="button"
                    onClick={() => setMaxDistanceKm(25)}
                    className={`px-2 py-0.5 rounded-md transition ${
                      maxDistanceKm === 25 ? 'bg-lime-400 text-black shadow' : 'bg-indigo-900 text-indigo-300 hover:text-white'
                    }`}
                  >
                    25 km
                  </button>
                  <button
                    type="button"
                    onClick={() => setMaxDistanceKm(50)}
                    className={`px-2 py-0.5 rounded-md transition ${
                      maxDistanceKm >= 50 ? 'bg-lime-400 text-black shadow' : 'bg-indigo-900 text-indigo-300 hover:text-white'
                    }`}
                  >
                    Any
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <span className="text-[9px] font-mono text-indigo-300/80 shrink-0">1 km</span>
                <input
                  type="range"
                  min={1}
                  max={50}
                  step={1}
                  value={maxDistanceKm}
                  onChange={(e) => setMaxDistanceKm(Number(e.target.value))}
                  className="w-full h-1.5 bg-indigo-900 rounded-lg appearance-none cursor-pointer accent-lime-400 focus:outline-none"
                />
                <span className="text-[9px] font-mono text-indigo-300/80 shrink-0">50+ km</span>
              </div>
            </div>

            {/* Sport Category Filter pills */}
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              <button
                onClick={() => setSportFilter('all')}
                className={`px-2.5 py-1 rounded-xl text-xs font-black italic uppercase transition ${
                  sportFilter === 'all' ? 'bg-lime-400 text-black' : 'bg-indigo-950 text-indigo-200/60 hover:text-white'
                }`}
              >
                All Sports
              </button>
              <button
                onClick={() => setSportFilter('basketball')}
                className={`px-2.5 py-1 rounded-xl text-xs font-black italic uppercase transition ${
                  sportFilter === 'basketball' ? 'bg-lime-400 text-black' : 'bg-indigo-950 text-indigo-200/60 hover:text-white'
                }`}
              >
                Basketball 🏀
              </button>
              <button
                onClick={() => setSportFilter('baseball')}
                className={`px-2.5 py-1 rounded-xl text-xs font-black italic uppercase transition ${
                  sportFilter === 'baseball' ? 'bg-lime-400 text-black' : 'bg-indigo-950 text-indigo-200/60 hover:text-white'
                }`}
              >
                Baseball ⚾
              </button>
              <button
                onClick={() => setSportFilter('softball')}
                className={`px-2.5 py-1 rounded-xl text-xs font-black italic uppercase transition ${
                  sportFilter === 'softball' ? 'bg-lime-400 text-black' : 'bg-indigo-950 text-indigo-200/60 hover:text-white'
                }`}
              >
                Softball 🥎
              </button>
              <button
                onClick={() => setSportFilter('pickleball')}
                className={`px-2.5 py-1 rounded-xl text-xs font-black italic uppercase transition ${
                  sportFilter === 'pickleball' ? 'bg-lime-400 text-black' : 'bg-indigo-950 text-indigo-200/60 hover:text-white'
                }`}
              >
                Pickleball 🏓
              </button>
              <button
                onClick={() => setSportFilter('soccer')}
                className={`px-2.5 py-1 rounded-xl text-xs font-black italic uppercase transition ${
                  sportFilter === 'soccer' ? 'bg-lime-400 text-black' : 'bg-indigo-950 text-indigo-200/60 hover:text-white'
                }`}
              >
                Soccer ⚽
              </button>
            </div>

            {/* Court Attribute & Facility Filters Section */}
            <div className="pt-2 border-t border-white/10 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => setShowAttributeFilterDrawer(!showAttributeFilterDrawer)}
                  className="flex items-center space-x-1.5 text-lime-400 font-black italic uppercase hover:text-lime-300 transition"
                >
                  <SlidersHorizontal className="w-3.5 h-3.5" />
                  <span>Attribute & Facility Filters</span>
                  {activeAttributeFiltersCount > 0 && (
                    <span className="px-1.5 py-0.2 bg-lime-400 text-black text-[9px] font-black font-mono rounded-full">
                      {activeAttributeFiltersCount}
                    </span>
                  )}
                </button>

                {activeAttributeFiltersCount > 0 && (
                  <button
                    type="button"
                    onClick={handleResetAttributeFilters}
                    className="text-[10px] text-rose-400 hover:text-rose-300 font-extrabold uppercase flex items-center space-x-1"
                  >
                    <X className="w-3 h-3" />
                    <span>Clear Attribute Filters</span>
                  </button>
                )}
              </div>

              {showAttributeFilterDrawer && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 pt-1 text-[11px] font-semibold">
                  {/* Indoor vs Outdoor */}
                  <div className="p-2 bg-indigo-950 rounded-xl border border-white/10 space-y-1">
                    <span className="text-[9px] text-indigo-300/70 uppercase font-black block">Location Type</span>
                    <div className="grid grid-cols-3 gap-1 text-[10px] text-center font-bold">
                      <button
                        onClick={() => setLocationTypeFilter('all')}
                        className={`py-1 rounded-lg transition ${
                          locationTypeFilter === 'all' ? 'bg-lime-400 text-black font-black' : 'text-indigo-200/70 hover:text-white'
                        }`}
                      >
                        All
                      </button>
                      <button
                        onClick={() => setLocationTypeFilter('indoor')}
                        className={`py-1 rounded-lg transition flex items-center justify-center space-x-0.5 ${
                          locationTypeFilter === 'indoor' ? 'bg-lime-400 text-black font-black' : 'text-indigo-200/70 hover:text-white'
                        }`}
                      >
                        <Building2 className="w-3 h-3 shrink-0" />
                        <span>Indoor</span>
                      </button>
                      <button
                        onClick={() => setLocationTypeFilter('outdoor')}
                        className={`py-1 rounded-lg transition flex items-center justify-center space-x-0.5 ${
                          locationTypeFilter === 'outdoor' ? 'bg-lime-400 text-black font-black' : 'text-indigo-200/70 hover:text-white'
                        }`}
                      >
                        <TreePine className="w-3 h-3 shrink-0" />
                        <span>Outdoor</span>
                      </button>
                    </div>
                  </div>

                  {/* Has Lights */}
                  <div className="p-2 bg-indigo-950 rounded-xl border border-white/10 space-y-1">
                    <span className="text-[9px] text-indigo-300/70 uppercase font-black block">Lighting</span>
                    <div className="grid grid-cols-3 gap-1 text-[10px] text-center font-bold">
                      <button
                        onClick={() => setLightingFilter('all')}
                        className={`py-1 rounded-lg transition ${
                          lightingFilter === 'all' ? 'bg-lime-400 text-black font-black' : 'text-indigo-200/70 hover:text-white'
                        }`}
                      >
                        All
                      </button>
                      <button
                        onClick={() => setLightingFilter('has_lights')}
                        className={`py-1 rounded-lg transition flex items-center justify-center space-x-0.5 ${
                          lightingFilter === 'has_lights' ? 'bg-lime-400 text-black font-black' : 'text-indigo-200/70 hover:text-white'
                        }`}
                      >
                        <Sun className="w-3 h-3 text-amber-300 shrink-0" />
                        <span>Lights 💡</span>
                      </button>
                      <button
                        onClick={() => setLightingFilter('no_lights')}
                        className={`py-1 rounded-lg transition ${
                          lightingFilter === 'no_lights' ? 'bg-lime-400 text-black font-black' : 'text-indigo-200/70 hover:text-white'
                        }`}
                      >
                        Day Only
                      </button>
                    </div>
                  </div>

                  {/* Rim Condition */}
                  <div className="p-2 bg-indigo-950 rounded-xl border border-white/10 space-y-1">
                    <span className="text-[9px] text-indigo-300/70 uppercase font-black block">Rim & Net Condition</span>
                    <select
                      value={rimFilter}
                      onChange={(e) => setRimFilter(e.target.value)}
                      className="w-full bg-indigo-900 text-white text-[10px] font-extrabold py-1 px-1.5 rounded-lg border border-white/10 outline-none focus:ring-1 focus:ring-lime-400"
                    >
                      <option value="all">All Rim Quality</option>
                      <option value="Double Rim">Double Rim</option>
                      <option value="Pro Breakaway Glass">Pro Glass / Breakaway</option>
                      <option value="Chain Net">Chain Net</option>
                      <option value="Padded Steel">Padded Steel</option>
                      <option value="Good">Good / Excellent</option>
                    </select>
                  </div>

                  {/* Amenities Toggles */}
                  <div className="p-2 bg-indigo-950 rounded-xl border border-white/10 space-y-1">
                    <span className="text-[9px] text-indigo-300/70 uppercase font-black block">Venue Amenities</span>
                    <div className="flex items-center space-x-1">
                      <button
                        type="button"
                        onClick={() => setWaterFountainOnly(!waterFountainOnly)}
                        className={`flex-1 py-1 px-1.5 rounded-lg text-[10px] font-black transition flex items-center justify-center space-x-1 border ${
                          waterFountainOnly
                            ? 'bg-lime-400 text-black border-lime-300 shadow-sm'
                            : 'bg-indigo-900 text-indigo-200/80 border-white/10 hover:text-white'
                        }`}
                      >
                        <Droplet className="w-3 h-3 text-cyan-400 shrink-0" />
                        <span>Water 🚰</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setParkingOnly(!parkingOnly)}
                        className={`flex-1 py-1 px-1.5 rounded-lg text-[10px] font-black transition flex items-center justify-center space-x-1 border ${
                          parkingOnly
                            ? 'bg-lime-400 text-black border-lime-300 shadow-sm'
                            : 'bg-indigo-900 text-indigo-200/80 border-white/10 hover:text-white'
                        }`}
                      >
                        <Car className="w-3 h-3 text-emerald-400 shrink-0" />
                        <span>Parking 🅿️</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Map Provider Toggle Bar */}
          <div className="flex items-center justify-between p-4 bg-indigo-950/90 border-b border-white/10 z-20 relative">
            <div className="flex items-center space-x-2">
              <span className="text-[10px] font-black uppercase text-indigo-300 font-mono">Map Engine:</span>
              <div className="flex items-center bg-black/60 p-1 rounded-xl border border-white/10 text-xs font-bold">
                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('light');
                    setMapMode('openstreetmap');
                  }}
                  className={`px-3 py-1 rounded-lg text-[11px] font-black italic uppercase transition flex items-center space-x-1 ${
                    mapMode === 'openstreetmap' ? 'bg-lime-400 text-black shadow' : 'text-indigo-200 hover:text-white'
                  }`}
                >
                  <Navigation className="w-3.5 h-3.5" />
                  <span>OpenStreetMap 🗺️</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('light');
                    setMapMode('grid');
                  }}
                  className={`px-3 py-1 rounded-lg text-[11px] font-black italic uppercase transition flex items-center space-x-1 ${
                    mapMode === 'grid' ? 'bg-lime-400 text-black shadow' : 'text-indigo-200 hover:text-white'
                  }`}
                >
                  <Layers className="w-3.5 h-3.5" />
                  <span>Interactive Grid 📍</span>
                </button>
              </div>
            </div>

            <div className="text-xs text-indigo-200/80 font-mono hidden sm:block">
              <span>Displaying {filteredCourts.length} Courts in {customCityFilter}, {customStateFilter}</span>
            </div>
          </div>

          {mapMode === 'openstreetmap' ? (
            <div className="p-4 relative z-10">
              <div
                id="court-openstreetmap-container"
                ref={mapContainerRef}
                className="w-full h-[520px] rounded-3xl overflow-hidden shadow-2xl border-2 border-lime-400/50 relative z-0"
              />
            </div>
          ) : (
            <>
              {/* Map Grid Background Canvas */}
              <div className="absolute inset-0 bg-[radial-gradient(#818cf8_1px,transparent_1px)] [background-size:24px_24px] opacity-25 pointer-events-none" />

              {/* Custom Interactive SVG Pins Grid with Dynamic Color Occupancy Levels & Mini-Popups */}
              <div className="relative my-auto py-8 px-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 z-10">
            {filteredCourts.map((court) => {
              const isSelected = selectedCourt.id === court.id;
              const isCheckedIn = checkedInCourts.includes(court.id);
              const extraPlayers = extraPlayersMap[court.id] || 0;
              const heat = getCourtHeatInfo(court, pickupGames, extraPlayers, isCheckedIn);
              const showPopup = activePopupCourtId === court.id;

              const activeCount = court.activePlayersNow !== undefined ? court.activePlayersNow + extraPlayers : heat.totalPlayers;
              const popularity = getPopularityBadge(activeCount);

              // Color resolution based on occupancy levels (Low / Medium / High)
              let svgPinFill = '#22c55e'; // Low (Green)
              let occupancyLevelText = 'LOW OCCUPANCY';
              let pinGlowHalo = 'shadow-emerald-500/30';

              if (heat.occupancyPct >= 80 || heat.totalPlayers >= 14) {
                svgPinFill = '#ef4444'; // High (Red)
                occupancyLevelText = 'HIGH OCCUPANCY';
                pinGlowHalo = 'shadow-rose-500/60 ring-2 ring-rose-500 animate-pulse';
              } else if (heat.occupancyPct >= 35 || heat.totalPlayers >= 5) {
                svgPinFill = '#f59e0b'; // Medium (Amber/Orange)
                occupancyLevelText = 'MEDIUM OCCUPANCY';
                pinGlowHalo = 'shadow-amber-400/40 ring-1 ring-amber-300';
              }

              const sportEmoji = 
                court.sport === 'basketball' ? '🏀' :
                court.sport === 'baseball' ? '⚾' :
                court.sport === 'softball' ? '🥎' :
                court.sport === 'pickleball' ? '🏓' :
                court.sport === 'soccer' ? '⚽' : '🏐';

              return (
                <div key={court.id} className="relative flex flex-col items-center group">
                  {/* Heat Glow Aura Ring */}
                  {showHeatGlowOverlay && (
                    <div
                      className={`absolute inset-x-4 inset-y-2 rounded-3xl opacity-35 blur-lg transition-all duration-500 pointer-events-none ${
                        heat.heatLevel === 'packed'
                          ? 'bg-rose-500 animate-pulse'
                          : heat.heatLevel === 'busy'
                          ? 'bg-orange-500'
                          : heat.heatLevel === 'moderate'
                          ? 'bg-amber-400'
                          : 'bg-emerald-500'
                      }`}
                    />
                  )}

                  {/* CUSTOM SVG MAP PIN HEAD & BADGE BUTTON */}
                  <div className="flex flex-col items-center z-10 w-full">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setSelectedCourt(court);
                        setActivePopupCourtId(showPopup ? null : court.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setSelectedCourt(court);
                          setActivePopupCourtId(showPopup ? null : court.id);
                        }
                      }}
                      className={`relative flex items-center space-x-2 px-3.5 py-2 rounded-2xl border transition-all duration-300 transform hover:scale-105 cursor-pointer ${
                        isSelected
                          ? 'bg-indigo-900/95 border-lime-400 ring-4 ring-lime-400/40 shadow-2xl scale-105'
                          : 'bg-indigo-950/90 border-white/15 hover:border-white/40 shadow-xl backdrop-blur-md'
                      }`}
                    >
                      {/* Subtle Ping Animation Ring on Selected Pins */}
                      {isSelected && (
                        <span className="absolute -inset-1.5 rounded-2xl bg-lime-400/40 animate-ping pointer-events-none" />
                      )}

                      {/* Custom SVG Teardrop Map Pin Icon */}
                      <div className="relative shrink-0 flex items-center justify-center">
                        {isSelected && (
                          <span className="absolute -inset-2 rounded-full bg-lime-400/50 animate-ping pointer-events-none" />
                        )}
                        <svg
                          width="36"
                          height="42"
                          viewBox="0 0 36 42"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="filter drop-shadow-md transition-colors duration-500"
                        >
                          <path
                            d="M18 0C8.05887 0 0 8.05887 0 18C0 29.5 16 41 18 42C20 41 36 29.5 36 18C36 8.05887 27.9411 0 18 0Z"
                            fill={svgPinFill}
                            stroke={isSelected ? '#ffffff' : '#00000044'}
                            strokeWidth={isSelected ? '2' : '1'}
                          />
                          <circle cx="18" cy="17" r="12" fill="#090d16" />
                        </svg>

                        {/* Center Sport Emoji */}
                        <span className="absolute top-1.5 inset-x-0 text-center text-xs select-none">
                          {sportEmoji}
                        </span>

                        {/* Live Active Player Count Badge on SVG Pin */}
                        <span
                          className="absolute -bottom-1 -right-1 px-1.5 py-0.2 rounded-full text-[9px] font-black text-black font-mono shadow border border-black/40"
                          style={{ backgroundColor: svgPinFill }}
                        >
                          {heat.totalPlayers}
                        </span>
                      </div>

                      {/* Pin Title & Occupancy Level Indicator */}
                      <div className="text-left min-w-0 pr-1">
                        <div className="flex items-center space-x-1.5">
                          <h4 className="font-extrabold text-xs text-white truncate group-hover:text-lime-300 transition">
                            {court.name}
                          </h4>
                          {isCheckedIn && (
                            <span className="bg-lime-400 text-black text-[8px] font-black uppercase px-1.5 py-0.2 rounded-full shrink-0">
                              Checked In
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                          <CourtStatusBadgeTooltip status={court.status || 'verified'} showText={true} />

                          <span
                            className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md"
                            style={{ backgroundColor: svgPinFill + '33', color: svgPinFill }}
                          >
                            {occupancyLevelText}
                          </span>

                          <span className={`text-[9px] font-black uppercase italic px-2 py-0.5 rounded-full border shadow-sm ${popularity.badgeStyle}`}>
                            {popularity.labelWithEmoji}
                          </span>
                        </div>
                      </div>

                      {/* Favorite Heart Quick Button & Chevron */}
                      <div className="flex items-center space-x-1 shrink-0 ml-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onToggleFavoriteCourt) onToggleFavoriteCourt(court.id);
                          }}
                          className={`p-1.5 rounded-xl border transition ${
                            favoriteCourtIds.includes(court.id)
                              ? 'bg-rose-500/20 text-rose-400 border-rose-500/50 hover:bg-rose-500/30'
                              : 'bg-indigo-950/60 text-indigo-300 border-white/10 hover:text-white hover:bg-indigo-900'
                          }`}
                          title={favoriteCourtIds.includes(court.id) ? 'Remove from Saved Favorites' : 'Save to Favorites'}
                        >
                          <Heart className={`w-3.5 h-3.5 ${favoriteCourtIds.includes(court.id) ? 'fill-rose-500 text-rose-500' : ''}`} />
                        </button>
                        <ChevronRight className={`w-4 h-4 text-indigo-300 transition-transform ${showPopup ? 'rotate-90 text-lime-400' : ''}`} />
                      </div>
                    </div>
                  </div>

                  {/* INTERACTIVE MINI-POPUP CARD (Appears when Pin is clicked) */}
                  {showPopup && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-80 z-30 bg-indigo-900/98 border-2 border-lime-400 rounded-3xl p-4 text-white shadow-2xl backdrop-blur-xl animate-fadeIn space-y-3">
                      {/* Mini-Popup Pointer Needle */}
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-indigo-900 border-t-2 border-l-2 border-lime-400 rotate-45" />

                      {/* Popup Header */}
                      <div className="flex items-start justify-between border-b border-white/10 pb-2 relative z-10">
                        <div className="flex items-center space-x-2 pr-2">
                          <span className="text-xl p-1.5 bg-indigo-950 rounded-xl border border-white/10">
                            {sportEmoji}
                          </span>
                          <div>
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span
                                className="text-[9px] font-black uppercase px-2 py-0.5 rounded-full"
                                style={{ backgroundColor: svgPinFill, color: '#000000' }}
                              >
                                {occupancyLevelText}
                              </span>
                              <span className={`text-[9px] font-black uppercase italic px-2 py-0.5 rounded-full border ${popularity.badgeStyle}`}>
                                {popularity.labelWithEmoji}
                              </span>
                            </div>
                            <h5 className="font-extrabold text-sm text-white line-clamp-1 mt-0.5">
                              {court.name}
                            </h5>
                          </div>
                        </div>

                        <div className="flex items-center space-x-1 shrink-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onToggleFavoriteCourt) onToggleFavoriteCourt(court.id);
                            }}
                            className={`p-1.5 rounded-full border transition ${
                              favoriteCourtIds.includes(court.id)
                                ? 'bg-rose-500/20 text-rose-400 border-rose-500/50 hover:bg-rose-500/30'
                                : 'bg-indigo-950 text-indigo-300 hover:text-white border border-white/10'
                            }`}
                            title={favoriteCourtIds.includes(court.id) ? 'Remove from Saved Favorites' : 'Save Court to Favorites'}
                          >
                            <Heart className={`w-4 h-4 ${favoriteCourtIds.includes(court.id) ? 'fill-rose-500 text-rose-500' : ''}`} />
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActivePopupCourtId(null);
                            }}
                            className="p-1.5 rounded-full bg-indigo-950 text-indigo-300 hover:text-white border border-white/10 transition"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Mini-Popup Active Player Count & Capacity Bar */}
                      <div className="bg-indigo-950 p-3 rounded-2xl border border-white/10 space-y-1.5 relative z-10">
                        <div className="flex items-center justify-between text-xs font-extrabold">
                          <span className="text-indigo-200 flex items-center">
                            <Users className="w-3.5 h-3.5 mr-1 text-lime-400" />
                            Active Athletes:
                          </span>
                          <span className="text-white font-mono text-sm">
                            {heat.totalPlayers} / {heat.maxCapacity} Max
                          </span>
                        </div>

                        <div className="w-full bg-indigo-900 h-2.5 rounded-full overflow-hidden border border-white/10">
                          <div
                            className="h-full transition-all duration-500 rounded-full"
                            style={{ width: `${heat.occupancyPct}%`, backgroundColor: svgPinFill }}
                          />
                        </div>

                        <div className="flex items-center justify-between text-[10px] text-indigo-300 font-semibold pt-0.5">
                          <span>{court.address}</span>
                          <span className="font-bold text-lime-300">{court.distanceKm} km</span>
                        </div>
                      </div>

                      {/* Mini-Popup Active Pickup Games indicator */}
                      {heat.activeGamesCount > 0 ? (
                        <div className="p-2 bg-rose-500/15 border border-rose-500/30 rounded-xl flex items-center justify-between text-xs font-bold text-rose-300 relative z-10">
                          <span className="flex items-center">
                            <Flame className="w-4 h-4 mr-1 text-rose-400 animate-pulse" />
                            {heat.activeGamesCount} Active Pickup Game{heat.activeGamesCount > 1 ? 's' : ''}
                          </span>
                          <span className="text-[10px] uppercase font-black text-rose-300">Live ⚡</span>
                        </div>
                      ) : (
                        <div className="p-2 bg-indigo-950 rounded-xl text-center text-[10px] text-indigo-300 font-bold relative z-10">
                          No active pickup games • Be the first to host!
                        </div>
                      )}

                      {/* Mini-Popup Quick Actions */}
                      <div className="flex flex-col gap-1.5 pt-1 relative z-10">
                        {/* Get Directions Link Button */}
                        <button
                          type="button"
                          onClick={(e) => handleGetDirections(court, e)}
                          className="w-full py-2 px-3 bg-gradient-to-r from-sky-400 to-blue-500 hover:from-sky-300 hover:to-blue-400 text-black font-black italic text-[11px] uppercase rounded-xl transition flex items-center justify-center space-x-1.5 shadow-md shadow-sky-500/20"
                        >
                          <Navigation className="w-3.5 h-3.5 fill-black/20 stroke-[2.5]" />
                          <span>Get Directions</span>
                        </button>

                        <div className="grid grid-cols-2 gap-1.5">
                          {isCheckedIn ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenLeaveModal(court);
                              }}
                              className="py-2 px-2 bg-rose-500 hover:bg-rose-600 text-white font-black italic text-[11px] uppercase rounded-xl transition flex items-center justify-center space-x-1 shadow-md shadow-rose-500/30"
                            >
                              <LogOut className="w-3.5 h-3.5 stroke-[2.5]" />
                              <span>Leave Court</span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCourt(court);
                                handleOpenQrScanner(court);
                              }}
                              className="py-2 px-2 bg-lime-400 hover:bg-lime-300 text-black font-black italic text-[11px] uppercase rounded-xl transition flex items-center justify-center space-x-1"
                            >
                              <QrCode className="w-3.5 h-3.5 stroke-[2.5]" />
                              <span>Check In</span>
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCourt(court);
                              onHostAtCourt(court);
                            }}
                            className="py-2 px-2 bg-indigo-950 hover:bg-indigo-800 text-white border border-white/20 font-black italic text-[11px] uppercase rounded-xl transition flex items-center justify-center space-x-1"
                          >
                            <Plus className="w-3.5 h-3.5 stroke-[3]" />
                            <span>Host Game</span>
                          </button>
                        </div>

                        {!isCheckedIn && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenLeaveModal(court);
                            }}
                            className="w-full py-1.5 px-2 bg-indigo-950 hover:bg-rose-500/20 text-rose-300 border border-rose-500/30 font-bold text-[10px] uppercase rounded-xl transition flex items-center justify-center space-x-1"
                          >
                            <LogOut className="w-3 h-3 text-rose-400" />
                            <span>Leave Court & Log Session</span>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Map Bottom Legend & GPS Footer */}
          <div className="z-10 bg-indigo-900/90 backdrop-blur-md p-3 rounded-2xl border border-white/10 flex flex-wrap items-center justify-between gap-2 text-xs text-indigo-200">
            <div className="flex items-center space-x-3 font-bold text-[11px]">
              <span className="flex items-center space-x-1">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
                <span>Quiet (Green)</span>
              </span>
              <span className="flex items-center space-x-1">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" />
                <span>Mod (Yellow)</span>
              </span>
              <span className="flex items-center space-x-1">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500 inline-block" />
                <span>Packed (Red)</span>
              </span>
            </div>

            <div className="flex items-center space-x-1 text-lime-400 font-black italic uppercase text-[10px]">
              <Compass className="w-3.5 h-3.5 text-lime-400" />
              <span>Real-Time Activity Engine Active</span>
            </div>
          </div>
        </>
      )}
    </div>

        {/* Right Selected Court Drawer (1 col) */}
        <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl flex flex-col justify-between space-y-6">
          <div className="space-y-4">
            <div className="relative rounded-2xl overflow-hidden h-44">
              <img
                src={selectedCourt.imageUrl}
                alt={selectedCourt.name}
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-indigo-950 via-indigo-950/40 to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 text-white flex items-end justify-between gap-2">
                <div>
                  <div className="flex items-center space-x-1.5">
                    <span className="px-2 py-0.5 rounded-full bg-lime-400 text-black text-[10px] font-black italic uppercase">
                      {selectedCourt.sport}
                    </span>
                    <span className={`px-2.5 py-0.5 font-black text-[10px] uppercase italic rounded-full border shadow-sm ${selectedPopularity.badgeStyle}`}>
                      Popularity: {selectedPopularity.labelWithEmoji}
                    </span>
                  </div>
                  <h3 className="text-lg font-black italic uppercase leading-tight mt-1">
                    {selectedCourt.name}
                  </h3>
                </div>

                <span className={`px-2.5 py-1 font-black text-[10px] uppercase rounded-xl border shrink-0 ${selectedCourtHeat.badgeBg}`}>
                  {selectedCourtHeat.badgeIcon} {selectedCourtHeat.occupancyPct}% Occupancy
                </span>
              </div>
            </div>

            {/* Address & Surface Details */}
            <div className="space-y-2 text-xs text-indigo-200/80">
              <div className="flex items-center justify-between gap-2">
                <p className="flex items-start min-w-0">
                  <MapPin className="w-4 h-4 mr-1.5 text-rose-400 shrink-0 mt-0.5" />
                  <span className="font-semibold text-white truncate">{selectedCourt.address}</span>
                </p>
                <button
                  type="button"
                  onClick={() => handleGetDirections(selectedCourt)}
                  className="px-2.5 py-1 bg-sky-400/20 hover:bg-sky-400 text-sky-300 hover:text-black border border-sky-400/40 font-black italic text-[10px] uppercase rounded-lg transition shrink-0 flex items-center space-x-1 shadow-sm"
                >
                  <Navigation className="w-3 h-3 stroke-[2.5]" />
                  <span>Directions</span>
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/10">
                <div className="p-2.5 rounded-xl bg-indigo-950 border border-white/10">
                  <span className="text-[10px] text-indigo-300/60 font-black uppercase block">Surface</span>
                  <span className="font-bold text-white line-clamp-1">{selectedCourt.surfaceType}</span>
                </div>
                <div className="p-2.5 rounded-xl bg-indigo-950 border border-white/10">
                  <span className="text-[10px] text-indigo-300/60 font-black uppercase block">Night Lights</span>
                  <span className="font-bold text-white">
                    {selectedCourt.lighting ? 'Yes 💡' : 'No ☀️'}
                  </span>
                </div>
              </div>
            </div>

            {/* Detailed Court Attributes Badges */}
            <div className="p-3 bg-indigo-950 rounded-2xl border border-white/10 space-y-2">
              <span className="text-[10px] text-indigo-300/60 font-black uppercase block tracking-wider">
                Facility & Equipment Specs
              </span>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 rounded-xl bg-indigo-900/60 border border-white/10 flex items-center space-x-1.5">
                  {selectedCourt.isIndoor ? <Building2 className="w-3.5 h-3.5 text-lime-400 shrink-0" /> : <TreePine className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                  <div>
                    <span className="text-[9px] text-indigo-300/70 font-black uppercase block">Type</span>
                    <span className="font-bold text-white">{selectedCourt.isIndoor ? 'Indoor Facility' : 'Outdoor Park'}</span>
                  </div>
                </div>

                <div className="p-2 rounded-xl bg-indigo-900/60 border border-white/10 flex items-center space-x-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                  <div>
                    <span className="text-[9px] text-indigo-300/70 font-black uppercase block">Rim Quality</span>
                    <span className="font-bold text-white">{selectedCourt.rimCondition || 'Double Rim'}</span>
                  </div>
                </div>

                <div className="p-2 rounded-xl bg-indigo-900/60 border border-white/10 flex items-center space-x-1.5">
                  <Droplet className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                  <div>
                    <span className="text-[9px] text-indigo-300/70 font-black uppercase block">Water Fountain</span>
                    <span className="font-bold text-white">{selectedCourt.waterFountain ? 'Available 🚰' : 'None'}</span>
                  </div>
                </div>

                <div className="p-2 rounded-xl bg-indigo-900/60 border border-white/10 flex items-center space-x-1.5">
                  <Car className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                  <div>
                    <span className="text-[9px] text-indigo-300/70 font-black uppercase block">Parking</span>
                    <span className="font-bold text-white">{selectedCourt.parkingAvailable ? 'On-Site Lot 🅿️' : 'Street'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Real-Time Heat & Capacity Meter */}
            <div className="p-4 bg-indigo-950 border border-white/10 rounded-2xl space-y-2 shadow-inner">
              <div className="flex items-center justify-between">
                <span className="text-xs font-black italic uppercase text-white flex items-center space-x-1">
                  <Activity className="w-4 h-4 text-lime-400" />
                  <span>Court Heat & Occupancy</span>
                </span>
                <span className={`text-xs font-black italic uppercase ${selectedCourtHeat.textTailwind}`}>
                  {selectedCourtHeat.badgeLabel}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-center text-xs font-bold py-2 bg-indigo-900/60 rounded-xl">
                <div>
                  <span className="text-[9px] text-indigo-300/60 block uppercase font-black">Active Athletes</span>
                  <span className="text-white text-sm font-black italic">{selectedCourtHeat.totalPlayers}</span>
                </div>
                <div>
                  <span className="text-[9px] text-indigo-300/60 block uppercase font-black">Capacity Limit</span>
                  <span className="text-indigo-200 text-sm font-black italic">{selectedCourtHeat.maxCapacity}</span>
                </div>
              </div>

              <div className="w-full bg-indigo-900 h-2.5 rounded-full overflow-hidden border border-white/10">
                <div
                  className={`h-full transition-all duration-500 ${selectedCourtHeat.bgTailwind}`}
                  style={{ width: `${selectedCourtHeat.occupancyPct}%` }}
                />
              </div>

              {/* Popularity Badge Status */}
              <div className="flex items-center justify-between text-xs pt-2 border-t border-white/10">
                <span className="text-indigo-300 font-bold flex items-center space-x-1">
                  <Sparkles className="w-3.5 h-3.5 text-lime-400" />
                  <span>Popularity Badge:</span>
                </span>
                <span className={`px-2.5 py-0.5 rounded-lg text-[10px] font-black uppercase italic border ${selectedPopularity.badgeStyle}`}>
                  {selectedPopularity.labelWithEmoji} ({selectedActiveCount} active now)
                </span>
              </div>

              {/* Real-Time Live Player Arrival Simulator Controls */}
              <div className="pt-2 flex items-center justify-between text-[11px] border-t border-white/10">
                <span className="text-indigo-300 font-bold">Simulate Live Arrival:</span>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handleSimulateArrival(selectedCourt.id, 3)}
                    className="px-2 py-1 bg-lime-400 hover:bg-lime-300 text-black font-black uppercase rounded-lg text-[10px] transition"
                  >
                    +3 Players 👥
                  </button>
                  <button
                    onClick={() => handleSimulateArrival(selectedCourt.id, -3)}
                    className="px-2 py-1 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 font-black uppercase rounded-lg text-[10px] transition"
                  >
                    -3
                  </button>
                </div>
              </div>
            </div>

            {/* Check-In & QR Code Scanner Status Action Box */}
            <div className="p-4 bg-indigo-950 border border-lime-400/30 rounded-2xl space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs font-black italic text-lime-400 uppercase block flex items-center space-x-1">
                    <QrCode className="w-4 h-4 mr-1 text-lime-400" />
                    <span>{checkedInCourts.includes(selectedCourt.id) ? 'Checked In & Verified!' : 'On-Site QR Check-In'}</span>
                  </span>
                  <span className="text-[10px] text-indigo-200/70 font-medium">
                    {checkedInCourts.includes(selectedCourt.id) 
                      ? 'Broadcast notification active for this court'
                      : 'Scan venue QR code to verify arrival & alert nearby players'}
                  </span>
                </div>

                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${
                  checkedInCourts.includes(selectedCourt.id) ? 'bg-lime-400 text-black' : 'bg-indigo-900 text-indigo-300'
                }`}>
                  {checkedInCourts.includes(selectedCourt.id) ? 'Active' : 'Unverified'}
                </span>
              </div>

              <div className="flex items-center space-x-2 pt-1">
                <button
                  onClick={() => handleOpenQrScanner(selectedCourt)}
                  className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-black italic uppercase transition flex items-center justify-center space-x-2 shadow-lg ${
                    checkedInCourts.includes(selectedCourt.id)
                      ? 'bg-indigo-900 text-indigo-200 border border-white/10 hover:bg-indigo-800'
                      : 'bg-lime-400 hover:bg-lime-300 text-black shadow-lime-400/20'
                  }`}
                >
                  <Camera className="w-4 h-4" />
                  <span>{checkedInCourts.includes(selectedCourt.id) ? 'Re-Scan QR Code' : 'Scan Court QR'}</span>
                </button>

                {!checkedInCourts.includes(selectedCourt.id) ? (
                  <button
                    onClick={() => handleCheckIn(selectedCourt.id)}
                    className="px-3 py-2.5 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 font-bold text-xs uppercase rounded-xl border border-white/10 transition"
                  >
                    Quick Check-In
                  </button>
                ) : (
                  <button
                    onClick={() => handleOpenLeaveModal(selectedCourt)}
                    className="px-3 py-2.5 bg-rose-500 hover:bg-rose-600 text-white font-black text-xs uppercase rounded-xl shadow-md transition flex items-center space-x-1"
                  >
                    <LogOut className="w-3.5 h-3.5 stroke-[2.5]" />
                    <span>Leave</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Drawer Actions */}
          <div className="space-y-2 pt-4 border-t border-white/10">
            {/* Save / Favorite Toggle Button */}
            <button
              type="button"
              onClick={() => {
                if (onToggleFavoriteCourt) onToggleFavoriteCourt(selectedCourt.id);
              }}
              className={`w-full py-2.5 px-4 rounded-2xl font-black italic text-xs uppercase shadow-md flex items-center justify-center space-x-2 transition border ${
                favoriteCourtIds.includes(selectedCourt.id)
                  ? 'bg-rose-500/20 text-rose-300 border-rose-500/50 hover:bg-rose-500/30'
                  : 'bg-indigo-900/80 hover:bg-indigo-800 text-white border-white/10'
              }`}
            >
              <Heart className={`w-4 h-4 ${favoriteCourtIds.includes(selectedCourt.id) ? 'fill-rose-500 text-rose-500' : ''}`} />
              <span>{favoriteCourtIds.includes(selectedCourt.id) ? 'Saved in Favorites ❤️' : 'Save Court to Favorites'}</span>
            </button>

            {/* Get Directions Link Button */}
            <button
              onClick={() => handleGetDirections(selectedCourt)}
              className="w-full py-3 px-4 bg-gradient-to-r from-sky-400 to-blue-500 hover:from-sky-300 hover:to-blue-400 text-black font-black italic text-xs uppercase rounded-2xl shadow-lg shadow-sky-400/20 flex items-center justify-center space-x-2 transition"
            >
              <Navigation className="w-4 h-4 fill-black/20 stroke-[2.5]" />
              <span>Get Directions (Maps App)</span>
            </button>

            {/* Leave Court & Log Review Dedicated Button */}
            <button
              onClick={() => handleOpenLeaveModal(selectedCourt)}
              className="w-full py-2.5 px-4 bg-rose-500/15 hover:bg-rose-500 text-rose-300 hover:text-white border border-rose-500/40 font-black italic text-xs uppercase rounded-2xl transition flex items-center justify-center space-x-2 shadow-md"
            >
              <LogOut className="w-4 h-4 stroke-[2.5]" />
              <span>Leave Court & Log Session Intensity</span>
            </button>

            <button
              onClick={() => onHostAtCourt(selectedCourt)}
              className="w-full py-3 px-4 bg-lime-400 hover:bg-lime-300 text-black font-black italic text-xs uppercase rounded-2xl shadow-lg shadow-lime-400/20 flex items-center justify-center space-x-2 transition"
            >
              <Plus className="w-4 h-4 stroke-[3]" />
              <span>Host Pickup Game Here</span>
            </button>

            <button
              onClick={() => setIsLeaderboardModalOpen(true)}
              className="w-full py-3 px-4 bg-gradient-to-r from-lime-400 to-emerald-400 hover:from-lime-300 hover:to-emerald-300 text-black font-black text-xs uppercase tracking-wider rounded-xl transition shadow-lg flex items-center justify-center space-x-2"
            >
              <Trophy className="w-4 h-4 stroke-[2.5]" />
              <span>Open Court-Specific Leaderboard Modal 👑</span>
            </button>

            <button
              onClick={() => onTriggerGeofenceAlert(selectedCourt)}
              className="w-full py-2.5 px-4 bg-indigo-950 hover:bg-indigo-900 text-indigo-200 border border-white/10 font-bold text-xs rounded-xl transition flex items-center justify-center space-x-1.5"
            >
              <Zap className="w-3.5 h-3.5 text-rose-400" />
              <span>Trigger Proximity Radar Alert</span>
            </button>

            {/* Court-Specific Leaderboard Embedded */}
            <div className="pt-2">
              <CourtSpecificLeaderboard
                court={selectedCourt}
                athletes={athletes}
                onSelectAthlete={onSelectAthlete}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

      {/* Court-Specific Leaderboard Modal Overlay */}
      {isLeaderboardModalOpen && (
        <CourtSpecificLeaderboard
          court={selectedCourt}
          athletes={athletes}
          onSelectAthlete={(ath) => {
            if (onSelectAthlete) onSelectAthlete(ath);
            setIsLeaderboardModalOpen(false);
          }}
          onClose={() => setIsLeaderboardModalOpen(false)}
          isModal={true}
        />
      )}

      {/* QR Code Scanner Interactive Modal */}
      {isQrModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-indigo-950 border border-lime-400/40 w-full max-w-lg rounded-[2.5rem] p-6 shadow-2xl relative space-y-5 text-white">
            
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center space-x-2.5">
                <div className="p-2.5 bg-lime-400 text-black rounded-2xl shadow-md">
                  <QrCode className="w-6 h-6 stroke-[2.5]" />
                </div>
                <div>
                  <h3 className="text-lg font-black italic uppercase tracking-tight text-white flex items-center">
                    <span>Court QR Code Scanner</span>
                    <span className="ml-2 px-2 py-0.5 bg-lime-400/20 border border-lime-400/40 text-lime-400 text-[9px] font-black rounded-full">
                      GPS + QR
                    </span>
                  </h3>
                  <p className="text-xs text-indigo-200/70 font-semibold">
                    Scan venue QR code upon arrival to check in & broadcast live alert
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsQrModalOpen(false)}
                className="p-2 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 rounded-xl transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Venue Selector in Scanner */}
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-indigo-300 tracking-wider">
                Target Court Venue
              </label>
              <select
                value={targetQrCourt.id}
                onChange={(e) => {
                  const found = courts.find((c) => c.id === e.target.value);
                  if (found) setTargetQrCourt(found);
                }}
                className="w-full px-3.5 py-2.5 bg-indigo-900/90 text-white text-xs rounded-xl border border-white/10 font-bold outline-none focus:ring-2 focus:ring-lime-400"
              >
                {courts.map((court) => (
                  <option key={court.id} value={court.id} className="bg-indigo-950 text-white">
                    {court.name} ({court.sport.toUpperCase()}) — {court.distanceKm} km
                  </option>
                ))}
              </select>
            </div>

            {/* Simulated Live Camera Scanner Viewfinder */}
            <div className="relative bg-black rounded-3xl overflow-hidden border-2 border-indigo-800 aspect-video flex flex-col items-center justify-center p-4 group">
              {/* Camera status badge overlay */}
              <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-md px-2.5 py-1 rounded-full border border-white/10 flex items-center space-x-1.5 text-[10px] font-mono text-lime-400">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                <span>CAMERA FEED: ONLINE</span>
              </div>

              <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-md px-2.5 py-1 rounded-full border border-white/10 text-[10px] font-mono text-indigo-300">
                GPS: {targetQrCourt.distanceKm} km
              </div>

              {/* Viewfinder Reticle Target Box */}
              <div className="relative w-44 h-44 border-2 border-dashed border-lime-400/80 rounded-2xl flex items-center justify-center p-2 bg-lime-400/5 shadow-2xl">
                {/* Corner Corner Brackets */}
                <div className="absolute top-0 left-0 w-4 h-4 border-t-4 border-l-4 border-lime-400 rounded-tl-lg" />
                <div className="absolute top-0 right-0 w-4 h-4 border-t-4 border-r-4 border-lime-400 rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-4 border-l-4 border-lime-400 rounded-bl-lg" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b-4 border-r-4 border-lime-400 rounded-br-lg" />

                {/* Laser Scanning Line Animation */}
                {isSimulatingScan && (
                  <div className="absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-lime-400 to-transparent shadow-lg shadow-lime-400 animate-pulse top-1/2 -translate-y-1/2" />
                )}

                {/* Generated QR Code Graphic SVG */}
                <div className="w-32 h-32 bg-white p-2 rounded-xl flex items-center justify-center shadow-inner">
                  <svg className="w-full h-full text-black" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2 2h8v8H2V2zm2 2v4h4V4H4zm0 10h8v8H2v-8zm2 2v4h4v-4H4zm10-14h8v8h-8V2zm2 2v4h4V4h-4zm-2 10h2v2h-2v-2zm4 0h2v2h-2v-2zm-2 2h2v2h-2v-2zm4 0h2v2h-2v-2zm0-4h2v2h-2v-2zm-4 4h2v4h-2v-4zm6 0h2v4h-2v-4z" />
                  </svg>
                </div>
              </div>

              {/* Status Hint */}
              <span className="text-[10px] text-indigo-300 font-mono mt-3 uppercase tracking-wider text-center">
                {isSimulatingScan ? '⚡ Validating QR Hash & Broadcasting Arrival...' : 'Align court QR code inside viewfinder frame'}
              </span>
            </div>

            {/* Notification preview info */}
            <div className="p-3 bg-indigo-900/60 rounded-2xl border border-white/10 text-xs space-y-1">
              <div className="flex items-center justify-between text-indigo-200">
                <span className="font-bold flex items-center">
                  <Bell className="w-3.5 h-3.5 text-lime-400 mr-1.5" />
                  Auto-Broadcast Target:
                </span>
                <span className="font-extrabold text-lime-400">
                  {(targetQrCourt.activePlayersNow || 0) + 4} Nearby Athletes
                </span>
              </div>
              <p className="text-[10px] text-indigo-300/80">
                Checking in via QR sends a live push notification to all players checked in at <strong className="text-white">{targetQrCourt.name}</strong>, letting them know you are ready for pickup games.
              </p>
            </div>

            {/* Scan Action Button */}
            <button
              onClick={() => handleExecuteQrCheckIn(targetQrCourt)}
              disabled={isSimulatingScan}
              className="w-full py-3.5 px-4 bg-lime-400 hover:bg-lime-300 text-black font-black italic text-sm uppercase rounded-2xl shadow-xl shadow-lime-400/20 flex items-center justify-center space-x-2 transition disabled:opacity-50"
            >
              {isSimulatingScan ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Verifying QR & Broadcasting...</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  <span>Scan QR & Trigger Broadcast Check-In (+50 XP)</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Leave Court & Session Intensity Review Modal */}
      {isLeaveModalOpen && leaveTargetCourt && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-indigo-950 border-2 border-rose-500/60 w-full max-w-lg rounded-[2.5rem] p-6 shadow-2xl relative space-y-5 text-white overflow-hidden">
            
            {/* Modal Ambient Glow */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />

            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 pb-4 relative z-10">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-rose-500 text-white rounded-2xl shadow-lg ring-2 ring-rose-400/50">
                  <LogOut className="w-6 h-6 stroke-[2.5]" />
                </div>
                <div>
                  <h3 className="text-lg font-black italic uppercase tracking-tight text-white flex items-center gap-2">
                    <span>Check-Out & Session Log</span>
                    <span className="px-2 py-0.5 bg-rose-500/20 text-rose-300 border border-rose-500/40 text-[9px] font-mono font-black rounded-full">
                      LIVE HEAT UPDATE
                    </span>
                  </h3>
                  <p className="text-xs text-indigo-300/80 font-semibold mt-0.5">
                    Leaving <strong className="text-white">{leaveTargetCourt.name}</strong> • Real-time occupancy will decrement
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsLeaveModalOpen(false)}
                className="p-1.5 rounded-full bg-indigo-900 text-indigo-300 hover:text-white border border-white/10 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleExecuteCheckout} className="space-y-4 relative z-10">
              {/* Session Intensity Selector */}
              <div className="space-y-2">
                <label className="text-xs font-black uppercase text-indigo-200 tracking-wider flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Activity className="w-4 h-4 text-lime-400" />
                    <span>Session Workout Intensity</span>
                  </span>
                  <span className="text-[10px] text-lime-300 font-mono">Select Run Pace</span>
                </label>

                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'Casual', label: 'Casual Run', sub: 'Light shooting / shootaround', color: 'border-emerald-400 bg-emerald-950/40 text-emerald-300', icon: '🟢' },
                    { key: 'Moderate', label: 'Moderate Sweat', sub: 'Half-court / active drills', color: 'border-amber-400 bg-amber-950/40 text-amber-300', icon: '🟡' },
                    { key: 'High Intensity', label: 'High Intensity', sub: 'Fast 5v5 full court run', color: 'border-orange-400 bg-orange-950/40 text-orange-300', icon: '🟧' },
                    { key: 'Pro Competitive', label: 'Pro Competitive', sub: 'Hardcore playoff vibe', color: 'border-rose-500 bg-rose-950/40 text-rose-300', icon: '🔴' },
                  ].map((item) => {
                    const isSelected = sessionIntensity === item.key;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => setSessionIntensity(item.key as any)}
                        className={`p-3 rounded-2xl border text-left transition-all duration-200 ${
                          isSelected
                            ? `${item.color} ring-2 ring-offset-2 ring-offset-indigo-950 shadow-lg scale-[1.02]`
                            : 'bg-indigo-900/60 border-white/10 text-indigo-200/80 hover:border-white/30'
                        }`}
                      >
                        <div className="flex items-center justify-between text-xs font-black">
                          <span>{item.icon} {item.label}</span>
                          {isSelected && <CheckCircle2 className="w-4 h-4 text-lime-400" />}
                        </div>
                        <p className="text-[10px] text-indigo-300/70 mt-1 font-medium line-clamp-1">
                          {item.sub}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 5-Star Session Quality Rating */}
              <div className="p-3.5 bg-indigo-900/60 rounded-2xl border border-white/10 space-y-2">
                <div className="flex items-center justify-between text-xs font-black">
                  <span className="text-white flex items-center gap-1.5">
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                    <span>Rate Today's Court Session Quality</span>
                  </span>
                  <span className="text-amber-400 font-mono font-extrabold text-sm">
                    {sessionRating} / 5 Stars
                  </span>
                </div>

                <div className="flex items-center justify-center space-x-2 py-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setSessionRating(star)}
                      className="p-1 transition transform hover:scale-125 focus:outline-none"
                    >
                      <Star
                        className={`w-7 h-7 ${
                          star <= sessionRating
                            ? 'text-amber-400 fill-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]'
                            : 'text-indigo-950 fill-indigo-950 stroke-indigo-700'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Court Conditions & Highlights Quick Tags */}
              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase text-indigo-200 tracking-wider block">
                  Court Conditions & Highlights
                </label>

                <div className="flex flex-wrap gap-1.5">
                  {[
                    'Rims Clean',
                    'Great Competition',
                    'Packed Run',
                    'Courts Dry',
                    'Lights ON',
                    'Good Vibe',
                    'Water Working',
                    'Easy Parking',
                  ].map((tag) => {
                    const isSelected = selectedTags.includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleConditionTag(tag)}
                        className={`px-3 py-1 rounded-xl text-[10px] font-extrabold uppercase transition border ${
                          isSelected
                            ? 'bg-lime-400 text-black border-lime-300 shadow'
                            : 'bg-indigo-900/80 text-indigo-300 border-white/10 hover:text-white'
                        }`}
                      >
                        {isSelected ? '✓ ' : '+ '}{tag}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Optional Review Comment Note */}
              <div className="space-y-1">
                <label className="text-xs font-black uppercase text-indigo-200 tracking-wider flex items-center space-x-1">
                  <MessageSquare className="w-3.5 h-3.5 text-lime-400" />
                  <span>Session Review / Notes (Optional)</span>
                </label>
                <textarea
                  value={sessionReviewNote}
                  onChange={(e) => setSessionReviewNote(e.target.value)}
                  placeholder="e.g. Ran 5 games straight! Good crowd, rims were solid..."
                  rows={2}
                  className="w-full bg-indigo-900/90 border border-white/15 rounded-xl p-3 text-xs text-white placeholder-indigo-300/50 outline-none focus:border-lime-400 transition resize-none"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsLeaveModalOpen(false)}
                  className="flex-1 py-3 px-4 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 font-bold text-xs uppercase rounded-2xl border border-white/10 transition"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="flex-1 py-3 px-4 bg-rose-500 hover:bg-rose-600 text-white font-black italic text-xs uppercase rounded-2xl shadow-xl shadow-rose-500/20 flex items-center justify-center space-x-2 transition"
                >
                  <LogOut className="w-4 h-4 stroke-[2.5]" />
                  <span>Confirm Check-Out (+25 XP)</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
